import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import * as api from './api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Archetype info
const ARCHETYPES = {
  conservative: { name: 'Консерватор', emoji: '🛡', color: '#0ea5e9' },
  balanced: { name: 'Балансировщик', emoji: '⚖️', color: '#8b5cf6' },
  growth: { name: 'Искатель роста', emoji: '📈', color: '#10b981' },
  aggressive: { name: 'Агрессивный', emoji: '🚀', color: '#f59e0b' },
  analyst: { name: 'Аналитик', emoji: '🔍', color: '#6366f1' },
};

const STATUS_LABELS = {
  started: 'Начал',
  in_progress: 'В процессе',
  completed: 'Завершил',
  not_started: 'Не начинал',
};

const ADMIN_TABS = [
  { id: 'dashboard', label: '📊 Дашборд' },
  { id: 'users', label: '👥 Пользователи' },
  { id: 'crm', label: '📤 CRM' },
  { id: 'reminders', label: '⏰ Напоминания' },
  { id: 'config', label: '🤖 Конфигурация' },
  { id: 'settings', label: '⚙️ Настройки' },
];

const CONFIG_SECTIONS = [
  { id: 'welcome', label: '👋 Приветствие' },
  { id: 'questions', label: '❓ Вопросы' },
  { id: 'notifications', label: '📢 Уведомления' },
  { id: 'rules', label: '🔀 Правила' },
  { id: 'variants', label: '🎯 Варианты' },
];

const ADMIN_TAB_IDS = new Set(ADMIN_TABS.map(tab => tab.id));
const CONFIG_SECTION_IDS = new Set(CONFIG_SECTIONS.map(section => section.id));
const DEFAULT_ADMIN_ROUTE = { tab: 'dashboard', configSection: 'welcome' };

function normalizeAdminRoute(route = DEFAULT_ADMIN_ROUTE) {
  const tab = ADMIN_TAB_IDS.has(route.tab) ? route.tab : DEFAULT_ADMIN_ROUTE.tab;
  const configSection = CONFIG_SECTION_IDS.has(route.configSection)
    ? route.configSection
    : DEFAULT_ADMIN_ROUTE.configSection;
  return { tab, configSection };
}

function parseAdminRoute(hash) {
  const normalizedHash = (hash || '').replace(/^#/, '').replace(/^\/+|\/+$/g, '');
  if (!normalizedHash) {
    return { ...DEFAULT_ADMIN_ROUTE };
  }

  const [tab, configSection] = normalizedHash.split('/');
  return normalizeAdminRoute({ tab, configSection });
}

function buildAdminHash(route) {
  const normalizedRoute = normalizeAdminRoute(route);
  if (normalizedRoute.tab === 'config') {
    return `#/${normalizedRoute.tab}/${normalizedRoute.configSection}`;
  }
  return `#/${normalizedRoute.tab}`;
}

function isSameAdminRoute(left, right) {
  const normalizedLeft = normalizeAdminRoute(left);
  const normalizedRight = normalizeAdminRoute(right);
  return normalizedLeft.tab === normalizedRight.tab && normalizedLeft.configSection === normalizedRight.configSection;
}

// Keep tab URLs shareable without requiring server-side SPA fallback.
function syncAdminHash(route, { replace = false } = {}) {
  if (typeof window === 'undefined') return;

  const nextHash = buildAdminHash(route);
  if (window.location.hash === nextHash) return;

  const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
  if (replace) {
    window.history.replaceState(null, '', nextUrl);
    return;
  }

  window.location.hash = nextHash;
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!api.getToken());
  const [route, setRoute] = useState(() => {
    if (typeof window === 'undefined') return { ...DEFAULT_ADMIN_ROUTE };
    return parseAdminRoute(window.location.hash);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Multibot state
  const [availableBots, setAvailableBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(() => {
    try { return localStorage.getItem('LEADBOT_ALIAS') || 'all'; } catch { return 'all'; }
  });

  // Data state
  const [stats, setStats] = useState(null);
  const [crmStats, setCrmStats] = useState(null);
  const [reminderStats, setReminderStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [countryFilter, setCountryFilter] = useState('');
  const [countriesList, setCountriesList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [botConfig, setBotConfig] = useState(null); // Current bot configuration
  const currentTab = route.tab;
  const currentConfigSection = route.configSection;

  const navigateAdmin = useCallback((nextRoute, options = {}) => {
    const mergedRoute = typeof nextRoute === 'function'
      ? nextRoute(route)
      : { ...route, ...nextRoute };
    const normalizedRoute = normalizeAdminRoute(mergedRoute);
    syncAdminHash(normalizedRoute, options);
    setRoute(prevRoute => isSameAdminRoute(prevRoute, normalizedRoute) ? prevRoute : normalizedRoute);
  }, [route]);

  // Load available bots
  const loadBots = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await api.getLeadbotBots();
      console.log('Loaded bots:', data);
      // Ensure bots have proper format - include botUsername for chat URL
      const bots = (data.bots || []).map(bot => {
        if (typeof bot === 'string') {
          return { alias: bot, displayName: bot, botUsername: bot, missingToken: false };
        }
        return {
          alias: bot.alias || bot.name || 'unknown',
          displayName: bot.displayName || bot.alias || bot.name || 'Unknown Bot',
          botUsername: bot.botUsername || bot.alias || bot.name || 'unknown',
          missingToken: bot.missingToken || false,
        };
      });
      // Always include leadbot if not present
      if (!bots.find(b => b.alias === 'leadbot')) {
        bots.unshift({ alias: 'leadbot', displayName: 'Lead Bot', botUsername: 'your_finance_way_bot' });
      }
      setAvailableBots(bots);
    } catch (e) {
      console.error('Failed to load bots:', e);
      setAvailableBots([{ alias: 'leadbot', displayName: 'Lead Bot', botUsername: 'your_finance_way_bot' }]);
    }
  }, [isAuthenticated]);

  // Load available bots on mount
  useEffect(() => {
    loadBots();
  }, [loadBots]);

  // Persist selected bot
  useEffect(() => {
    try { localStorage.setItem('LEADBOT_ALIAS', selectedBot); } catch { }
  }, [selectedBot]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleHashChange = () => {
      const nextRoute = parseAdminRoute(window.location.hash);
      setRoute(prevRoute => isSameAdminRoute(prevRoute, nextRoute) ? prevRoute : nextRoute);

      if (window.location.hash !== buildAdminHash(nextRoute)) {
        syncAdminHash(nextRoute, { replace: true });
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Load bot config when selectedBot changes
  useEffect(() => {
    if (!isAuthenticated || selectedBot === 'all') {
      setBotConfig(null);
      return;
    }
    api.getBotConfig(selectedBot)
      .then(data => setBotConfig(data.config || {}))
      .catch(() => setBotConfig(null));
  }, [isAuthenticated, selectedBot]);

  // Load data based on tab (includes selectedBot dependency)
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        switch (currentTab) {
          case 'dashboard':
            const [statsData, crmData] = await Promise.all([
              api.getStats({ alias: selectedBot }),
              api.getCrmStats({ alias: selectedBot }),
            ]);
            setStats(statsData);
            setCrmStats(crmData);
            break;

          case 'users':
            const usersParams = { page: usersPage, limit: 20, alias: selectedBot };
            if (countryFilter) usersParams.country = countryFilter;
            const [usersData, countriesData] = await Promise.all([
              api.getUsers(usersParams),
              api.getCountries({ alias: selectedBot }),
            ]);
            setUsers(usersData.users);
            setUsersTotal(usersData.total);
            setCountriesList(countriesData.countries || []);
            break;

          case 'crm':
            const crmStatsData = await api.getCrmStats({ alias: selectedBot });
            setCrmStats(crmStatsData);
            break;

          case 'reminders':
            const reminderData = await api.getReminderStats();
            setReminderStats(reminderData);
            break;

          case 'settings':
            const webhookData = await api.getWebhookInfo();
            setWebhookInfo(webhookData);
            break;
        }
      } catch (err) {
        setError(err.message);
        if (err.message === 'Unauthorized') {
          setIsAuthenticated(false);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, currentTab, usersPage, selectedBot, countryFilter]);

  // Login handler
  const handleLogin = async (token) => {
    setLoading(true);
    setError(null);

    try {
      const valid = await api.verifyToken(token);
      if (valid) {
        setIsAuthenticated(true);
      } else {
        setError('Неверный токен');
      }
    } catch (err) {
      setError('Ошибка подключения');
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    api.clearToken();
    setIsAuthenticated(false);
    setStats(null);
    setCrmStats(null);
    setUsers([]);
    setSelectedUser(null);
  };

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} loading={loading} error={error} />;
  }

  return (
    <div className="app">
      <Header
        onLogout={handleLogout}
        availableBots={availableBots}
        selectedBot={selectedBot}
        onSelectBot={setSelectedBot}
        onRefreshBots={loadBots}
      />

      <nav className="nav-tabs">
        {ADMIN_TABS.map(tab => (
          <button
            key={tab.id}
            className={`nav-tab ${currentTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              navigateAdmin({ tab: tab.id });
              setSelectedUser(null);
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="main">
        {loading && <Loading />}
        {error && <div className="card" style={{ color: 'var(--danger)' }}>Ошибка: {error}</div>}

        {!loading && !error && (
          <>
            {currentTab === 'dashboard' && <DashboardTab stats={stats} crmStats={crmStats} scoringEnabled={botConfig?.scoringEnabled !== false} />}
            {currentTab === 'users' && (
              selectedUser ? (
                <UserDetail user={selectedUser} onBack={() => setSelectedUser(null)} />
              ) : (
                <UsersTab
                  users={users}
                  total={usersTotal}
                  page={usersPage}
                  onPageChange={setUsersPage}
                  onSelectUser={setSelectedUser}
                  onDeleteUser={(telegramId) => {
                    setUsers(users.filter(u => u.telegram_id !== telegramId));
                    setUsersTotal(usersTotal - 1);
                  }}
                  showBotAlias={selectedBot === 'all'}
                  scoringEnabled={botConfig?.scoringEnabled !== false}
                  botAliasToUsername={Object.fromEntries(availableBots.map(b => [b.alias, b.botUsername]))}
                  countryFilter={countryFilter}
                  countriesList={countriesList}
                  onCountryFilter={(c) => { setCountryFilter(c); setUsersPage(1); }}
                  selectedBot={selectedBot}
                />
              )
            )}
            {currentTab === 'crm' && <CrmTab stats={crmStats} onRetry={() => api.retryCrmExports()} />}
            {currentTab === 'reminders' && <RemindersTab stats={reminderStats} />}
            {currentTab === 'config' && (
              <BotConfigTab
                selectedBot={selectedBot}
                activeSection={currentConfigSection}
                onSectionChange={(section) => navigateAdmin({ tab: 'config', configSection: section })}
              />
            )}
            {currentTab === 'settings' && <SettingsTab webhookInfo={webhookInfo} selectedBot={selectedBot} />}
          </>
        )}
      </main>
    </div>
  );
}

// Login Page
function LoginPage({ onLogin, loading, error }) {
  const [token, setToken] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (token.trim()) {
      onLogin(token.trim());
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">📊 Lead Bot Admin</h1>

        <form onSubmit={handleSubmit}>
          <div className="login-input">
            <label>Пароль</label>
            <input
              type="password"
              className="input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Введите пароль"
              autoFocus
            />
          </div>

          {error && <p className="login-error">{error}</p>}

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Проверка...' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Header
function Header({ onLogout, availableBots, selectedBot, onSelectBot, onRefreshBots }) {
  const [showAddBot, setShowAddBot] = useState(false);
  const [newBotAlias, setNewBotAlias] = useState('');
  const [newBotDisplayName, setNewBotDisplayName] = useState('');
  const [newBotToken, setNewBotToken] = useState('');
  const [addingBot, setAddingBot] = useState(false);
  const [addError, setAddError] = useState('');
  const [addStep, setAddStep] = useState(''); // '', 'saving', 'webhook'

  const handleAddBot = async () => {
    if (!newBotAlias.trim()) return;
    if (!newBotToken.trim()) {
      setAddError('Токен бота обязателен!');
      return;
    }
    setAddingBot(true);
    setAddError('');

    try {
      const alias = newBotAlias.trim().toLowerCase();

      // Step 1: Save bot token to KV
      setAddStep('Сохранение токена...');
      await api.saveBot(alias, newBotToken.trim(), newBotDisplayName.trim() || alias);

      // Step 2: Setup webhook for the bot
      setAddStep('Настройка webhook...');
      await api.setupBotWebhook(alias, newBotToken.trim());

      // Refresh bots list
      onRefreshBots?.();

      // Select the new bot
      onSelectBot(alias);

      // Close modal
      setShowAddBot(false);
      setNewBotAlias('');
      setNewBotDisplayName('');
      setNewBotToken('');
      setAddStep('');
    } catch (err) {
      setAddError(err.message || 'Ошибка добавления');
      setAddStep('');
    } finally {
      setAddingBot(false);
    }
  };

  return (
    <header className="header">
      <h1>📊 Lead Bot Admin</h1>
      <div className="header-actions">
        <select
          className="bot-selector"
          value={selectedBot}
          onChange={(e) => onSelectBot(e.target.value)}
          style={{ marginRight: '0.5rem', padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid var(--gray-300)', background: 'white', fontWeight: 500 }}
        >
          <option value="all">📊 Все боты</option>
          {availableBots.map((bot, idx) => {
            const alias = typeof bot === 'object' ? (bot.alias || `bot-${idx}`) : String(bot);
            const name = typeof bot === 'object' ? (bot.displayName || bot.alias || alias) : String(bot);
            const isBroken = typeof bot === 'object' && bot.missingToken;
            return (
              <option key={alias} value={alias}>
                {isBroken ? '⚠️' : '🤖'} {name}{isBroken ? ' (нет токена!)' : ''}
              </option>
            );
          })}
        </select>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowAddBot(true)}
          style={{ marginRight: '1rem' }}
          title="Добавить бота"
        >
          ➕
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onLogout}>
          Выйти
        </button>
      </div>

      {/* Add Bot Modal */}
      {showAddBot && (
        <div className="modal-overlay" onClick={() => setShowAddBot(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>➕ Добавить бота</h2>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
              Создайте конфигурацию для нового бота. Получите токен у @BotFather в Telegram.
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                🔑 Токен бота от @BotFather *
              </label>
              <input
                className="input"
                type="password"
                value={newBotToken}
                onChange={(e) => setNewBotToken(e.target.value)}
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                style={{ fontFamily: 'monospace' }}
                autoFocus
              />
              <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
                Создайте бота через @BotFather и скопируйте токен
              </small>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                📝 Alias (идентификатор) *
              </label>
              <input
                className="input"
                value={newBotAlias}
                onChange={(e) => setNewBotAlias(e.target.value.replace(/[^a-z0-9_-]/gi, '').toLowerCase())}
                placeholder="например: mybot"
              />
              <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
                Латиница, без пробелов. Используется для идентификации бота в системе.
              </small>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                🏷️ Отображаемое имя
              </label>
              <input
                className="input"
                value={newBotDisplayName}
                onChange={(e) => setNewBotDisplayName(e.target.value)}
                placeholder="например: Мой Бот"
              />
            </div>

            {addError && <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>❌ {addError}</p>}
            {addStep && <p style={{ color: 'var(--primary)', marginBottom: '1rem' }}>⏳ {addStep}</p>}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowAddBot(false)} disabled={addingBot}>
                Отмена
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddBot}
                disabled={!newBotAlias.trim() || !newBotToken.trim() || addingBot}
              >
                {addingBot ? '⏳ Добавление...' : '✅ Добавить бота'}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// Loading
function Loading() {
  return (
    <div className="loading">
      <div className="spinner"></div>
    </div>
  );
}

// Dashboard Tab
function DashboardTab({ stats, crmStats, scoringEnabled = true }) {
  if (!stats) return null;

  const funnelData = stats.funnel || {};
  const total = funnelData.total || 1;

  const archetypeData = {
    labels: (stats.byArchetype || []).map(a => ARCHETYPES[a.archetype]?.name || a.archetype),
    datasets: [{
      data: (stats.byArchetype || []).map(a => a.count),
      backgroundColor: (stats.byArchetype || []).map(a => ARCHETYPES[a.archetype]?.color || '#888'),
    }],
  };

  const statusData = {
    labels: (stats.byStatus || []).map(s => STATUS_LABELS[s.survey_status] || s.survey_status),
    datasets: [{
      label: 'Пользователи',
      data: (stats.byStatus || []).map(s => s.count),
      backgroundColor: '#2563eb',
    }],
  };

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Всего пользователей</div>
          <div className="stat-value">{stats.totalUsers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Завершили опрос</div>
          <div className="stat-value success">{funnelData.completed_survey || 0}</div>
          <div className="stat-change">
            {total > 0 ? Math.round((funnelData.completed_survey || 0) / total * 100) : 0}% конверсия
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Отправлено в CRM</div>
          <div className="stat-value primary">{crmStats?.exported || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Заблокировано</div>
          <div className="stat-value danger">{crmStats?.blocked || 0}</div>
        </div>
      </div>

      {/* Funnel */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Воронка конверсии</h3>
        </div>
        <div className="funnel">
          {[
            { label: 'Всего пользователей', value: funnelData.total, key: 'total' },
            { label: 'Начали опрос', value: funnelData.started_survey, key: 'started' },
            { label: 'Завершили опрос', value: funnelData.completed_survey, key: 'completed' },
            { label: 'Дали телефон', value: funnelData.gave_phone, key: 'phone' },
            { label: 'Экспорт в CRM', value: funnelData.exported_crm, key: 'crm' },
          ].map((step, i) => (
            <div key={step.key} className="funnel-step">
              <span className="funnel-label">{step.label}</span>
              <div className="funnel-bar">
                <div
                  className="funnel-fill"
                  style={{ width: `${total > 0 ? (step.value / total * 100) : 0}%` }}
                />
              </div>
              <span className="funnel-value">{step.value || 0}</span>
              <span className="funnel-percent">
                {total > 0 ? Math.round((step.value || 0) / total * 100) : 0}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {scoringEnabled && (stats.byArchetype || []).length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Архетипы</h3>
            </div>
            <div className="chart-container">
              <Doughnut
                data={archetypeData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom' } },
                }}
              />
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Статусы опроса</h3>
          </div>
          <div className="chart-container">
            <Bar
              data={statusData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// Country ISO → flag emoji helper
function countryToFlag(iso) {
  if (!iso || iso.length !== 2) return '';
  const code = iso.toUpperCase();
  return String.fromCodePoint(
    ...[...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
}

// Broadcast Modal
function BroadcastModal({ alias, onClose }) {
  const [message, setMessage] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('photo');
  const [buttons, setButtons] = useState([]);
  const [recipientCount, setRecipientCount] = useState(null);
  const [testTelegramId, setTestTelegramId] = useState('');
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.getBroadcastCount(alias).then(data => setRecipientCount(data.count)).catch(() => {});
    api.getBroadcastHistory({ alias }).then(data => setHistory(data.logs || [])).catch(() => {});
  }, [alias]);

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons([...buttons, { text: '', value: '', type: 'url' }]);
  };

  const updateButton = (index, field, value) => {
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    setButtons(updated);
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const getValidButtons = () => buttons.filter(b => b.text && b.value);

  const handleTestSend = async () => {
    if (!message.trim() || !testTelegramId.trim()) return;
    setSending(true);
    setTestResult(null);
    try {
      const result = await api.sendBroadcastTest({
        message: message.trim(),
        mediaUrl: mediaUrl.trim() || undefined,
        mediaType: mediaUrl.trim() ? mediaType : undefined,
        buttons: getValidButtons().length > 0 ? getValidButtons() : undefined,
        alias,
        telegramId: testTelegramId.trim(),
      });
      if (result.success) {
        setTestResult({ ok: true, text: '✅ Тестовое сообщение отправлено!' });
      } else {
        setTestResult({ ok: false, text: `❌ ${result.error}${result.blocked ? ' (бот заблокирован)' : ''}` });
      }
    } catch (e) {
      setTestResult({ ok: false, text: `❌ ${e.message}` });
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = async () => {
    if (!message.trim()) return;
    const count = recipientCount ?? '?';
    if (!confirm(`📢 Отправить рассылку ${count} пользователям бота "${alias}"?\n\nЭто действие нельзя отменить.`)) return;

    setSending(true);
    setBroadcastResult(null);
    try {
      const result = await api.sendBroadcast({
        message: message.trim(),
        mediaUrl: mediaUrl.trim() || undefined,
        mediaType: mediaUrl.trim() ? mediaType : undefined,
        buttons: getValidButtons().length > 0 ? getValidButtons() : undefined,
        alias,
      });
      setBroadcastResult(result);
      // Refresh history
      api.getBroadcastHistory({ alias }).then(data => setHistory(data.logs || [])).catch(() => {});
    } catch (e) {
      setBroadcastResult({ success: false, error: e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'white', borderRadius: '12px', maxWidth: '640px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>📢 Рассылка — {alias}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--gray-400)' }}>✕</button>
        </div>

        {recipientCount !== null && (
          <div style={{ padding: '0.5rem 0.75rem', background: 'var(--gray-50)', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>
            📬 Получателей: <strong>{recipientCount}</strong>
            <span style={{ color: 'var(--gray-400)', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
              (исключены: без источника, заблокировавшие бот)
            </span>
          </div>
        )}

        {/* Message Text */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.9rem' }}>Текст сообщения (HTML):</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Привет! 👋\n\nУ нас для вас <b>важная новость</b>..."
            rows={5}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--gray-200)', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>

        {/* Media (Optional) */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.9rem' }}>Медиа (опционально):</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="URL фото/видео/анимации"
              style={{ flex: 1, padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--gray-200)', fontSize: '0.85rem' }}
            />
            <select
              value={mediaType}
              onChange={(e) => setMediaType(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--gray-200)', fontSize: '0.85rem' }}
            >
              <option value="photo">📷 Фото</option>
              <option value="video">🎬 Видео</option>
              <option value="animation">🎞 GIF</option>
            </select>
          </div>
        </div>

        {/* Inline URL Buttons */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.9rem' }}>Кнопки (макс. 3):</label>
          {buttons.map((btn, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
              <input
                value={btn.text}
                onChange={(e) => updateButton(i, 'text', e.target.value)}
                placeholder="Текст кнопки"
                style={{ flex: 1, padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid var(--gray-200)', fontSize: '0.85rem' }}
              />
              <select
                value={btn.type || 'url'}
                onChange={(e) => updateButton(i, 'type', e.target.value)}
                style={{ padding: '0.4rem 0.3rem', borderRadius: '6px', border: '1px solid var(--gray-200)', fontSize: '0.8rem', minWidth: '90px' }}
              >
                <option value="url">🔗 URL</option>
                <option value="copy_text">📋 Копировать</option>
              </select>
              <input
                value={btn.value}
                onChange={(e) => updateButton(i, 'value', e.target.value)}
                placeholder={btn.type === 'copy_text' ? 'Текст для копирования' : 'https://...'}
                style={{ flex: 1.5, padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid var(--gray-200)', fontSize: '0.85rem' }}
              />
              <button onClick={() => removeButton(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'var(--danger)' }}>✕</button>
            </div>
          ))}
          {buttons.length < 3 && (
            <button onClick={addButton} className="btn btn-secondary btn-sm" style={{ fontSize: '0.8rem' }}>
              + Добавить кнопку
            </button>
          )}
        </div>

        <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid var(--gray-200)' }} />

        {/* Test Send */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.9rem' }}>🧪 Тестовая отправка:</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={testTelegramId}
              onChange={(e) => setTestTelegramId(e.target.value)}
              placeholder="Telegram ID"
              style={{ flex: 1, padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--gray-200)', fontSize: '0.85rem' }}
            />
            <button
              className="btn btn-secondary"
              onClick={handleTestSend}
              disabled={sending || !message.trim() || !testTelegramId.trim()}
              style={{ whiteSpace: 'nowrap' }}
            >
              {sending ? '...' : '🧪 Тест'}
            </button>
          </div>
          {testResult && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: testResult.ok ? 'var(--success)' : 'var(--danger)' }}>
              {testResult.text}
            </div>
          )}
        </div>

        <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid var(--gray-200)' }} />

        {/* Broadcast Result */}
        {broadcastResult && (
          <div style={{ padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', background: broadcastResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${broadcastResult.success ? '#bbf7d0' : '#fecaca'}` }}>
            {broadcastResult.success ? (
              <div style={{ fontSize: '0.9rem' }}>
                <strong>✅ Рассылка завершена!</strong>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <span>📤 Всего: <strong>{broadcastResult.total}</strong></span>
                  <span>✅ Отправлено: <strong>{broadcastResult.sent}</strong></span>
                  {broadcastResult.failed > 0 && <span>❌ Ошибок: <strong>{broadcastResult.failed}</strong></span>}
                  {broadcastResult.blocked > 0 && <span>🚫 Заблокировали: <strong>{broadcastResult.blocked}</strong></span>}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.9rem', color: 'var(--danger)' }}>
                <strong>❌ Ошибка:</strong> {broadcastResult.error}
              </div>
            )}
          </div>
        )}

        {/* Send Button */}
        <button
          className="btn btn-primary"
          onClick={handleBroadcast}
          disabled={sending || !message.trim()}
          style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
        >
          {sending ? '⏳ Отправка...' : `📢 Отправить${recipientCount !== null ? ` ${recipientCount} пользователям` : ''}`}
        </button>

        {/* Broadcast History */}
        {history && history.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--gray-500)' }}>📋 История рассылок</h4>
            <div style={{ fontSize: '0.8rem' }}>
              {history.filter(h => !h.is_test).slice(0, 5).map(h => (
                <div key={h.id} style={{ padding: '0.4rem 0', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {h.message_text?.substring(0, 60)}{h.message_text?.length > 60 ? '...' : ''}
                  </span>
                  <span style={{ whiteSpace: 'nowrap', color: 'var(--gray-400)' }}>
                    ✅{h.sent_count}{h.blocked_count > 0 ? ` 🚫${h.blocked_count}` : ''}{h.failed_count > 0 ? ` ❌${h.failed_count}` : ''}
                  </span>
                  <span style={{ whiteSpace: 'nowrap', color: 'var(--gray-400)' }}>
                    {h.created_at ? new Date(h.created_at).toLocaleDateString('ru') : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Users Tab
function UsersTab({ users, total, page, onPageChange, onSelectUser, onDeleteUser, showBotAlias, scoringEnabled = true, botAliasToUsername = {}, countryFilter = '', countriesList = [], onCountryFilter, selectedBot }) {
  const [deleting, setDeleting] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const totalPages = Math.ceil(total / 20);

  const handleDelete = async (user) => {
    if (!confirm(`Удалить пользователя ${user.first_name || user.username || user.telegram_id}?\n\nЭто действие удалит все данные пользователя безвозвратно.`)) return;
    
    setDeleting(user.telegram_id);
    try {
      await api.deleteUser(user.telegram_id, user.bot_alias);
      onDeleteUser(user.telegram_id);
    } catch (e) {
      alert('Ошибка удаления: ' + e.message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h3 className="card-title" style={{ margin: 0 }}>Пользователи ({total})</h3>
        {onCountryFilter && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>Страна:</label>
            <select
              value={countryFilter}
              onChange={(e) => onCountryFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                borderRadius: '6px',
                border: '1px solid var(--gray-200)',
                fontSize: '0.85rem',
                background: 'white',
                cursor: 'pointer',
              }}
            >
              <option value="">Все страны</option>
              {countriesList.map(c => (
                <option key={c.country_code} value={c.country_code}>
                  {countryToFlag(c.country_code)} {c.country_code} ({c.count})
                </option>
              ))}
            </select>
            {countryFilter && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => onCountryFilter('')}
                title="Сбросить фильтр"
                style={{ padding: '2px 6px', fontSize: '0.8rem' }}
              >
                ✕
              </button>
            )}
          </div>
        )}
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowBroadcast(true)}
          disabled={!selectedBot || selectedBot === 'all'}
          title={!selectedBot || selectedBot === 'all' ? 'Выберите конкретного бота' : 'Массовая рассылка'}
          style={{ whiteSpace: 'nowrap' }}
        >
          📢 Рассылка
        </button>
      </div>

      {showBroadcast && selectedBot && selectedBot !== 'all' && (
        <BroadcastModal alias={selectedBot} onClose={() => setShowBroadcast(false)} />
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              {showBotAlias && <th>Бот</th>}
              <th>Имя</th>
              <th>Источник</th>
              <th>Статус</th>
              {scoringEnabled && <th>Архетип</th>}
              <th>Телефон</th>
              <th>Страна</th>
              <th>CRM</th>
              <th>Дата</th>
              <th></th>
              <th>Чат</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={`${user.bot_alias || 'leadbot'}-${user.id}`} style={{ cursor: 'pointer' }} onClick={() => onSelectUser(user)}>
                <td>{user.telegram_id}</td>
                {showBotAlias && (
                  <td>
                    <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: 999, background: 'var(--primary)', color: 'white', fontWeight: 500 }}>
                      {user.bot_alias || 'leadbot'}
                    </span>
                  </td>
                )}
                <td>
                  {user.first_name || ''} {user.last_name || ''}
                  {user.username && <span style={{ color: 'var(--gray-400)' }}> @{user.username}</span>}
                </td>
                <td>
                  {user.source ? (
                    <span style={{ fontSize: '0.875rem', padding: '2px 6px', borderRadius: 999, background: 'var(--gray-100)', border: '1px solid var(--gray-200)' }}>
                      {user.source.kind === 'deeplink' ? (user.source.label || user.source.payload || 'deeplink') : 'direct'}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--gray-400)' }}>—</span>
                  )}
                </td>
                <td>
                  <span className={`badge badge-${user.survey_status === 'completed' ? 'success' : user.survey_status === 'in_progress' ? 'warning' : 'gray'}`}>
                    {STATUS_LABELS[user.survey_status] || user.survey_status}
                  </span>
                </td>
                {scoringEnabled && (
                  <td>
                    {user.archetype && (
                      <span className={`archetype-${user.archetype}`}>
                        {ARCHETYPES[user.archetype]?.emoji} {ARCHETYPES[user.archetype]?.name}
                      </span>
                    )}
                  </td>
                )}
                <td>
                  {user.phone ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {user.phone}
                      {user.phone_input_type && (
                        <span 
                          title={user.phone_input_type === 'api' ? 'Через Telegram API' : user.phone_input_type === 'osint' ? 'Найден через OSINT' : 'Введён вручную'}
                          style={{ 
                            fontSize: '0.65rem', 
                            padding: '1px 4px', 
                            borderRadius: 4, 
                            background: user.phone_input_type === 'api' ? '#dcfce7' : user.phone_input_type === 'osint' ? '#dbeafe' : '#fef3c7',
                            color: user.phone_input_type === 'api' ? '#166534' : user.phone_input_type === 'osint' ? '#1e40af' : '#92400e',
                            fontWeight: 500
                          }}
                        >
                          {user.phone_input_type === 'api' ? 'API' : user.phone_input_type === 'osint' ? '🔍 OSINT' : 'Руч.'}
                        </span>
                      )}
                    </span>
                  ) : user.osint_phone ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: 'var(--gray-500)' }}>{user.osint_phone}</span>
                      <span 
                        title="Найден через OSINT"
                        style={{ 
                          fontSize: '0.65rem', 
                          padding: '1px 4px', 
                          borderRadius: 4, 
                          background: '#dbeafe',
                          color: '#1e40af',
                          fontWeight: 500
                        }}
                      >
                        🔍 OSINT
                      </span>
                    </span>
                  ) : '—'}
                </td>
                <td>
                  {user.country_code ? (
                    <span title={user.country_code} style={{ whiteSpace: 'nowrap' }}>
                      {countryToFlag(user.country_code)} {user.country_code}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--gray-400)' }}>—</span>
                  )}
                </td>
                <td>
                  {user.crm_exported ? (
                    <span className="badge badge-success">✓</span>
                  ) : user.crm_blocked ? (
                    <span className="badge badge-danger">Blocked</span>
                  ) : (
                    <span className="badge badge-gray">—</span>
                  )}
                </td>
                <td>{user.created_at ? new Date(user.created_at).toLocaleDateString('ru') : '—'}</td>
                <td>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => { e.stopPropagation(); onSelectUser(user); }}
                    title="Детали пользователя"
                  >
                    👤 Детали
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(user); }}
                    disabled={deleting === user.telegram_id}
                    title="Удалить пользователя"
                  >
                    {deleting === user.telegram_id ? '...' : '🗑'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            ←
          </button>
          <span className="pagination-info">
            Страница {page} из {totalPages}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

// User Detail
function UserDetail({ user, onBack }) {
  const [userDetails, setUserDetails] = useState(null);
  const [pendingNotifications, setPendingNotifications] = useState(null);
  const [forceSending, setForceSending] = useState(null);
  const [osintRetrying, setOsintRetrying] = useState(false);

  const loadDetails = async () => {
    try {
      const data = await api.getUser(user.telegram_id, user.bot_alias);
      setUserDetails(data);
      return data;
    } catch (e) {
      console.error('Failed to load user details:', e);
      return null;
    }
  };

  useEffect(() => {
    const loadPendingNotifications = async () => {
      try {
        const data = await api.getPendingNotifications(user.telegram_id, user.bot_alias);
        setPendingNotifications(data.pending);
      } catch (e) {
        console.error('Failed to load pending notifications:', e);
      }
    };

    loadDetails();
    loadPendingNotifications();
  }, [user.telegram_id]);

  // Auto-poll while OSINT status is pending/in_progress/queued
  useEffect(() => {
    const osintStatus = (userDetails?.user || user)?.osint_status;
    const activeStatuses = ['in_progress', 'pending', 'queued'];
    if (!activeStatuses.includes(osintStatus)) return;

    const interval = setInterval(async () => {
      const data = await loadDetails();
      const updated = data?.user?.osint_status;
      if (updated && !activeStatuses.includes(updated)) {
        clearInterval(interval);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [userDetails?.user?.osint_status, user.telegram_id]);

  const handleForceSend = async (notificationType) => {
    setForceSending(notificationType);
    try {
      const result = await api.forceNotification(user.telegram_id, notificationType, user.bot_alias);
      if (result.success) {
        alert('✅ Уведомление отправлено!');
        // Refresh pending notifications
        const data = await api.getPendingNotifications(user.telegram_id, user.bot_alias);
        setPendingNotifications(data.pending);
      } else {
        alert('❌ Ошибка: ' + result.message);
      }
    } catch (e) {
      alert('❌ Ошибка: ' + e.message);
    } finally {
      setForceSending(null);
    }
  };

  const handleOsintRetry = async () => {
    setOsintRetrying(true);
    try {
      const result = await api.retryOsint(user.telegram_id);
      if (result.success) {
        // Backend enqueues OSINT lookup via Cloudflare Queue.
        // Set status locally — auto-poll useEffect picks up the final result.
        setUserDetails(prev => {
          const base = prev?.user || user;
          return { ...prev, user: { ...base, osint_status: result.osint_status || 'queued' } };
        });
      } else {
        alert('❌ Ошибка: ' + (result.error || 'Unknown error'));
      }
    } catch (e) {
      alert('❌ Ошибка: ' + e.message);
    } finally {
      setOsintRetrying(false);
    }
  };

  const detail = userDetails?.user || user;

  return (
    <>
      <button className="btn btn-secondary" onClick={onBack} style={{ marginBottom: '1rem' }}>
        ← Назад к списку
      </button>

      <div className="user-detail">
        <div className="user-info">
          <div className="user-avatar">
            {detail.first_name?.[0] || '👤'}
          </div>
          <div className="user-name">{detail.first_name} {detail.last_name}</div>
          {detail.username && <div className="user-username">@{detail.username}</div>}

          <div className="user-meta">
            <div className="user-meta-item">
              <span className="user-meta-label">Telegram ID</span>
              <span className="user-meta-value">{detail.telegram_id}</span>
            </div>
            <div className="user-meta-item">
              <span className="user-meta-label">Статус</span>
              <span className="user-meta-value">
                <span className={`badge badge-${detail.survey_status === 'completed' ? 'success' : 'warning'}`}>
                  {STATUS_LABELS[detail.survey_status]}
                </span>
              </span>
            </div>
            {detail.archetype && (
              <div className="user-meta-item">
                <span className="user-meta-label">Архетип</span>
                <span className={`user-meta-value archetype-${detail.archetype}`}>
                  {ARCHETYPES[detail.archetype]?.emoji} {ARCHETYPES[detail.archetype]?.name}
                </span>
              </div>
            )}
            {detail.phone && (
              <div className="user-meta-item">
                <span className="user-meta-label">Телефон</span>
                <span className="user-meta-value">{detail.phone}</span>
              </div>
            )}
            <div className="user-meta-item">
              <span className="user-meta-label">CRM</span>
              <span className="user-meta-value">
                {detail.crm_exported ? '✅ Экспортирован' : detail.crm_blocked ? '🚫 Заблокирован' : '⏳ Ожидает'}
              </span>
            </div>
            <div className="user-meta-item">
              <span className="user-meta-label">Создан</span>
              <span className="user-meta-value">{new Date(detail.created_at).toLocaleString('ru')}</span>
            </div>
            {detail.utm_source && (
              <div className="user-meta-item">
                <span className="user-meta-label">UTM Source</span>
                <span className="user-meta-value">{detail.utm_source}</span>
              </div>
            )}
            {detail.utm_campaign && (
              <div className="user-meta-item">
                <span className="user-meta-label">UTM Campaign</span>
                <span className="user-meta-value">{detail.utm_campaign}</span>
              </div>
            )}
          </div>
        </div>

        {/* Deeplink Parameters Section */}
        {userDetails?.deeplink && (userDetails.deeplink.country || userDetails.deeplink.source || userDetails.deeplink.params) && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <h4 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>🔗 Deeplink параметры</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.5rem' }}>
              {userDetails.deeplink.country && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Страна (c)</span>
                  <div style={{ fontWeight: 500 }}>{userDetails.deeplink.country}</div>
                </div>
              )}
              {userDetails.deeplink.source && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Источник (s)</span>
                  <div style={{ fontWeight: 500 }}>{userDetails.deeplink.source}</div>
                </div>
              )}
              {userDetails.deeplink.version && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Версия (v)</span>
                  <div style={{ fontWeight: 500 }}>{userDetails.deeplink.version}</div>
                </div>
              )}
              {userDetails.deeplink.company && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Компания (cm)</span>
                  <div style={{ fontWeight: 500 }}>{userDetails.deeplink.company}</div>
                </div>
              )}
              {userDetails.deeplink.requestPhoneType && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Тип телефона (rpt)</span>
                  <div style={{ fontWeight: 500 }}>{userDetails.deeplink.requestPhoneType}</div>
                </div>
              )}
              {userDetails.deeplink.finalMessage && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Финальное сообщение (f)</span>
                  <div style={{ fontWeight: 500 }}>{userDetails.deeplink.finalMessage}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* OSINT Data Section */}
        {detail.osint_status && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: detail.osint_status === 'success' ? '#f0fdf4' : 'var(--gray-50)', borderRadius: '8px', border: `1px solid ${detail.osint_status === 'success' ? '#bbf7d0' : 'var(--gray-200)'}` }}>
            <h4 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              🔍 OSINT данные
              <span style={{
                fontSize: '0.7rem',
                padding: '2px 6px',
                borderRadius: 4,
                background: detail.osint_status === 'success' ? '#dcfce7' : detail.osint_status === 'not_found' ? '#fef3c7' : ['pending', 'in_progress', 'queued'].includes(detail.osint_status) ? '#dbeafe' : '#fee2e2',
                color: detail.osint_status === 'success' ? '#166534' : detail.osint_status === 'not_found' ? '#92400e' : ['pending', 'in_progress', 'queued'].includes(detail.osint_status) ? '#1e40af' : '#991b1b',
              }}>
                {detail.osint_status === 'success' ? '✅ Найден' : detail.osint_status === 'not_found' ? '❌ Не найден' : detail.osint_status === 'queued' ? '📨 В очереди' : detail.osint_status === 'pending' ? '⏳ Ожидает' : detail.osint_status === 'in_progress' ? '🔄 Проверяется...' : '⚠️ Ошибка'}
              </span>
              <button
                onClick={handleOsintRetry}
                disabled={osintRetrying}
                style={{
                  marginLeft: 'auto',
                  padding: '3px 10px',
                  fontSize: '0.75rem',
                  borderRadius: 4,
                  border: '1px solid var(--gray-300)',
                  background: 'white',
                  cursor: osintRetrying ? 'not-allowed' : 'pointer',
                  opacity: osintRetrying ? 0.6 : 1,
                }}
              >
                {osintRetrying ? '⏳ Запрос...' : '🔄 Повторить'}
              </button>
            </h4>
            {detail.osint_status === 'success' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem' }}>
                {detail.osint_phone && (
                  <div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>📱 Телефон</span>
                    <div style={{ fontWeight: 500 }}>{detail.osint_phone}</div>
                  </div>
                )}
                {detail.osint_country && (
                  <div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>🌍 Страна</span>
                    <div style={{ fontWeight: 500 }}>{detail.osint_country}</div>
                  </div>
                )}
                {(() => {
                  try {
                    const osintData = detail.osint_data ? JSON.parse(detail.osint_data) : {};
                    return (
                      <>
                        {osintData.registration_date && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>📅 Регистрация</span>
                            <div style={{ fontWeight: 500 }}>{osintData.registration_date}</div>
                          </div>
                        )}
                        {osintData.possible_locations && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>🌃 Локации</span>
                            <div style={{ fontWeight: 500 }}>{osintData.possible_locations}</div>
                          </div>
                        )}
                        {osintData.ip && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>📶 IP</span>
                            <div style={{ fontWeight: 500 }}>{osintData.ip}</div>
                          </div>
                        )}
                        {osintData.premium && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>⭐ Premium</span>
                            <div style={{ fontWeight: 500 }}>Да</div>
                          </div>
                        )}
                        {(osintData.reactions?.positive > 0 || osintData.reactions?.negative > 0) && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>👍 Реакции</span>
                            <div style={{ fontWeight: 500 }}>{osintData.reactions.positive} 👍 {osintData.reactions.negative} 👎</div>
                          </div>
                        )}
                        {osintData.interest_count > 0 && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>👁 Запросов</span>
                            <div style={{ fontWeight: 500 }}>{osintData.interest_count}</div>
                          </div>
                        )}
                        {osintData.profiles && osintData.profiles.length > 0 && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>👤 Профили</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                              {osintData.profiles.map((p, i) => (
                                <span key={i} style={{ fontSize: '0.8rem', padding: '2px 8px', background: '#e0e7ff', borderRadius: 4, color: '#3730a3' }}>
                                  {p.username ? `@${p.username}` : p.name || 'Unknown'}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* INN lookup fields */}
                        {osintData.people && osintData.people.length > 0 && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>👤 ФИО</span>
                            <div style={{ fontWeight: 500 }}>{osintData.people.join(', ')}</div>
                          </div>
                        )}
                        {osintData.birth_dates && osintData.birth_dates.length > 0 && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>🎂 Дата рождения</span>
                            <div style={{ fontWeight: 500 }}>{osintData.birth_dates.join(', ')}</div>
                          </div>
                        )}
                        {osintData.inn && osintData.inn.length > 0 && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>🏛 ИНН</span>
                            <div style={{ fontWeight: 500 }}>{osintData.inn.join(', ')}</div>
                          </div>
                        )}
                      </>
                    );
                  } catch { return null; }
                })()}
                {detail.osint_report_url && (
                  <div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>📋 Отчёт</span>
                    <div><a href={detail.osint_report_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem' }}>Открыть отчёт ↗</a></div>
                  </div>
                )}
              </div>
            )}
            {detail.osint_looked_up_at && (
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.5rem' }}>
                Проверено: {new Date(detail.osint_looked_up_at).toLocaleString('ru')}
              </div>
            )}
          </div>
        )}

        {/* Survey Answers Section */}
        {userDetails?.answers && userDetails.answers.length > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <h4 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>📝 Ответы на опрос</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {userDetails.answers.map((answer, idx) => (
                <div key={idx} style={{ padding: '0.5rem', background: 'white', borderRadius: '4px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        Вопрос: {answer.question_id}
                      </div>
                      <div style={{ fontWeight: 500 }}>{answer.answer_id}</div>
                    </div>
                    {answer.answer_score && (
                      <div style={{ textAlign: 'right', fontSize: '0.75rem' }}>
                        <div style={{ color: 'var(--gray-500)', marginBottom: '0.25rem' }}>Score</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', justifyContent: 'flex-end' }}>
                          {Object.entries(answer.answer_score).map(([key, value]) => (
                            <span
                              key={key}
                              style={{
                                padding: '0.125rem 0.375rem',
                                background: value > 0 ? '#dcfce7' : value < 0 ? '#fee2e2' : '#f3f4f6',
                                borderRadius: '4px',
                                fontSize: '0.625rem',
                                fontWeight: 500,
                              }}
                            >
                              {key.slice(0, 3)}: {value > 0 ? '+' : ''}{value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Notifications Section */}
        {pendingNotifications && (pendingNotifications.incompleteSurvey || pendingNotifications.completedNoName || pendingNotifications.completedNoPhone) && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <h4 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>📬 Запланированные уведомления</h4>

            {pendingNotifications.incompleteSurvey && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
                <div>
                  <span style={{ fontWeight: 500 }}>🔔 Незавершённый опрос</span>
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    ({pendingNotifications.incompleteSurvey.sent}/{pendingNotifications.incompleteSurvey.max})
                  </span>
                  {pendingNotifications.incompleteSurvey.nextAt && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      Следующее: {new Date(pendingNotifications.incompleteSurvey.nextAt).toLocaleString('ru')}
                    </div>
                  )}
                </div>
                {pendingNotifications.incompleteSurvey.canSend && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleForceSend('incomplete_survey')}
                    disabled={forceSending === 'incomplete_survey'}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {forceSending === 'incomplete_survey' ? '⏳' : '📤 Отправить'}
                  </button>
                )}
              </div>
            )}

            {pendingNotifications.completedNoName && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: pendingNotifications.completedNoPhone ? '1px solid var(--gray-200)' : 'none' }}>
                <div>
                  <span style={{ fontWeight: 500 }}>✍️ Ждём имя</span>
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    ({pendingNotifications.completedNoName.sent}/{pendingNotifications.completedNoName.max})
                  </span>
                  {pendingNotifications.completedNoName.nextAt && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      Следующее: {new Date(pendingNotifications.completedNoName.nextAt).toLocaleString('ru')}
                    </div>
                  )}
                </div>
                {pendingNotifications.completedNoName.canSend && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleForceSend('completed_no_name')}
                    disabled={forceSending === 'completed_no_name'}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {forceSending === 'completed_no_name' ? '⏳' : '📤 Отправить'}
                  </button>
                )}
              </div>
            )}

            {pendingNotifications.completedNoPhone && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0' }}>
                <div>
                  <span style={{ fontWeight: 500 }}>📱 Без телефона</span>
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    ({pendingNotifications.completedNoPhone.sent}/{pendingNotifications.completedNoPhone.max})
                  </span>
                  {pendingNotifications.completedNoPhone.nextAt && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      Следующее: {new Date(pendingNotifications.completedNoPhone.nextAt).toLocaleString('ru')}
                    </div>
                  )}
                </div>
                {pendingNotifications.completedNoPhone.canSend && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleForceSend('completed_no_phone')}
                    disabled={forceSending === 'completed_no_phone'}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {forceSending === 'completed_no_phone' ? '⏳' : '📤 Отправить'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// CRM Tab
function CrmTab({ stats, onRetry }) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const result = await onRetry();
      alert(`Обработано: ${result.processed}, успешно: ${result.success}, ошибок: ${result.failed}`);
    } catch (e) {
      alert('Ошибка: ' + e.message);
    } finally {
      setRetrying(false);
    }
  };

  if (!stats) return null;

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Всего лидов</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Экспортировано</div>
          <div className="stat-value success">{stats.exported}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Заблокировано</div>
          <div className="stat-value danger">{stats.blocked}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ожидают экспорта</div>
          <div className="stat-value warning">{stats.pending}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Действия</h3>
        </div>
        <button className="btn btn-primary" onClick={handleRetry} disabled={retrying}>
          {retrying ? 'Обработка...' : '🔄 Повторить неудачные экспорты'}
        </button>
      </div>

      {stats.byCountry?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">По странам</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Страна</th>
                  <th>Количество</th>
                </tr>
              </thead>
              <tbody>
                {stats.byCountry.map(item => (
                  <tr key={item.country_code || 'unknown'}>
                    <td>{item.country_code || 'Неизвестно'}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats.blockedReasons?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Причины блокировки</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Причина</th>
                  <th>Количество</th>
                </tr>
              </thead>
              <tbody>
                {stats.blockedReasons.map(item => (
                  <tr key={item.crm_block_reason || 'unknown'}>
                    <td>{item.crm_block_reason || 'Неизвестно'}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// Reminders Tab
function RemindersTab({ stats }) {
  if (!stats) return null;

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Получили напоминания</div>
          <div className="stat-value">{stats.receivedReminders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Отписались</div>
          <div className="stat-value danger">{stats.stoppedReminders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Конвертировались</div>
          <div className="stat-value success">{stats.convertedAfterReminder}</div>
        </div>
      </div>

      {stats.byVariant?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">A/B тест напоминаний</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Вариант</th>
                  <th>Всего</th>
                  <th>Конверсия</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {stats.byVariant.map(item => (
                  <tr key={item.variant}>
                    <td>Вариант {item.variant}</td>
                    <td>{item.total}</td>
                    <td>{item.converted}</td>
                    <td>
                      <strong>
                        {item.total > 0 ? Math.round(item.converted / item.total * 100) : 0}%
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// Bot Configuration Tab
function BotConfigTab({ selectedBot, activeSection = DEFAULT_ADMIN_ROUTE.configSection, onSectionChange }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const currentSection = CONFIG_SECTION_IDS.has(activeSection) ? activeSection : DEFAULT_ADMIN_ROUTE.configSection;
  
  // OSINT settings (stored separately in KV, not in botconfig)
  const [osintSettings, setOsintSettings] = useState({ enabled: false, hasApiKey: false, apiKeyMasked: null });
  const [osintApiKey, setOsintApiKey] = useState('');
  const [osintSaving, setOsintSaving] = useState(false);

  const isAllBots = selectedBot === 'all';

  useEffect(() => {
    if (isAllBots) return;
    setLoading(true);
    setError(null);
    api.getBotConfig(selectedBot)
      .then(data => {
        console.log('Loaded config:', data);
        setConfig(data.config || {});
        setLoading(false);
      })
      .catch(e => {
        console.error('Failed to load config:', e);
        setError(e.message);
        setLoading(false);
      });
    // Load OSINT settings from KV
    api.getOsintSettings().then(data => {
      setOsintSettings(data);
    }).catch(() => {});
  }, [selectedBot, isAllBots]);

  // Show message if "all" is selected
  if (isAllBots) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h3>Выберите конкретного бота</h3>
        <p style={{ color: 'var(--gray-500)' }}>
          Для настройки конфигурации нужно выбрать конкретного бота в выпадающем списке вверху страницы.
        </p>
      </div>
    );
  }

  // Helper for safe toggle updates (uses PATCH to avoid data loss)
  const handleToggleChange = async (field, value) => {
    try {
      const result = await api.patchBotConfig(selectedBot, { [field]: value });
      setConfig(result.config);
    } catch (e) {
      console.error(`Failed to update ${field}:`, e);
      alert(`❌ Ошибка: ${e.message}`);
      // Revert local state
      setConfig(prev => ({ ...prev, [field]: !value }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      console.log('Saving config for bot:', selectedBot);
      console.log('Config to save:', config);
      const result = await api.updateBotConfig(selectedBot, config);
      console.log('Save result:', result);
      setConfig(result.config);
      alert('✅ Конфигурация сохранена!');
    } catch (e) {
      // Check if it's a data loss warning
      if (e.message.includes('Data loss detected') || e.message.includes('would lose')) {
        const confirmSave = confirm(`⚠️ Обнаружена потенциальная потеря данных!\n\n${e.message}\n\nВсё равно сохранить?`);
        if (confirmSave) {
          try {
            const result = await api.updateBotConfig(selectedBot, { ...config, _confirmDataLoss: true });
            setConfig(result.config);
            alert('✅ Конфигурация сохранена!');
            return;
          } catch (e2) {
            setError(e2.message);
            alert('❌ Ошибка сохранения: ' + e2.message);
          }
        }
      } else {
        setError(e.message);
        alert('❌ Ошибка сохранения: ' + e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Сбросить конфигурацию к значениям по умолчанию?')) return;
    setSaving(true);
    try {
      const result = await api.resetBotConfig(selectedBot);
      setConfig(result.config);
      alert('✅ Конфигурация сброшена!');
    } catch (e) {
      alert('❌ Ошибка: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (index, field, value) => {
    setConfig(prev => {
      const questions = [...(prev.questions || [])];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  const updateQuestionOption = (qIndex, oIndex, field, value) => {
    setConfig(prev => {
      const questions = [...(prev.questions || [])];
      const options = [...(questions[qIndex].options || [])];
      options[oIndex] = { ...options[oIndex], [field]: value };
      questions[qIndex] = { ...questions[qIndex], options };
      return { ...prev, questions };
    });
  };

  const addQuestion = () => {
    const questions = [...(config.questions || [])];
    const newIndex = questions.length + 1;
    questions.push({
      id: `q${newIndex}_new`,
      text: `❓ <b>Вопрос ${newIndex}</b>\n\nНовый вопрос`,
      options: [
        { id: `q${newIndex}_opt1`, text: 'Вариант 1', score: {} },
        { id: `q${newIndex}_opt2`, text: 'Вариант 2', score: {} },
      ],
    });
    setConfig({ ...config, questions });
  };

  const removeQuestion = (index) => {
    if (!confirm('Удалить вопрос?')) return;
    const questions = [...(config.questions || [])];
    questions.splice(index, 1);
    setConfig({ ...config, questions });
  };

  const addOption = (qIndex) => {
    const questions = [...(config.questions || [])];
    const options = [...(questions[qIndex].options || [])];
    options.push({ id: `${questions[qIndex].id}_opt${options.length + 1}`, text: 'Новый вариант', score: {} });
    questions[qIndex] = { ...questions[qIndex], options };
    setConfig({ ...config, questions });
  };

  const removeOption = (qIndex, oIndex) => {
    const questions = [...(config.questions || [])];
    const options = [...(questions[qIndex].options || [])];
    options.splice(oIndex, 1);
    questions[qIndex] = { ...questions[qIndex], options };
    setConfig({ ...config, questions });
  };

  const updateNotification = (key, field, value) => {
    setConfig(prev => {
      const notifications = { ...(prev.notifications || {}) };
      notifications[key] = { ...(notifications[key] || {}), [field]: value };
      return { ...prev, notifications };
    });
  };

  const addConditionalContent = () => {
    const key = prompt('Введите start параметр (например: ad_facebook):');
    if (!key) return;
    const conditionalContent = { ...(config.conditionalContent || {}) };
    conditionalContent[key] = {
      welcome: { text: 'Приветствие для ' + key, mediaUrl: null, mediaType: null },
    };
    setConfig({ ...config, conditionalContent });
  };

  const removeConditionalContent = (key) => {
    if (!confirm(`Удалить контент для "${key}"?`)) return;
    const conditionalContent = { ...(config.conditionalContent || {}) };
    delete conditionalContent[key];
    setConfig({ ...config, conditionalContent });
  };

  const updateConditionalContent = (condKey, msgKey, field, value) => {
    setConfig(prev => {
      const conditionalContent = { ...(prev.conditionalContent || {}) };
      conditionalContent[condKey] = { ...(conditionalContent[condKey] || {}) };
      conditionalContent[condKey][msgKey] = { ...(conditionalContent[condKey][msgKey] || {}), [field]: value };
      return { ...prev, conditionalContent };
    });
  };

  if (loading) return <div className="card"><Loading /></div>;
  if (error && !config) return <div className="card" style={{ color: 'var(--danger)' }}>Ошибка: {error}</div>;
  if (!config) return <div className="card">Конфигурация не найдена</div>;

  // Show message when "all" is selected - configuration is per-bot
  if (selectedBot === 'all') {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤖</div>
          <h3 style={{ marginBottom: '0.5rem' }}>Выберите конкретного бота</h3>
          <p style={{ color: 'var(--gray-500)' }}>
            Конфигурация настраивается для каждого бота отдельно.<br />
            Выберите бота в селекторе наверху страницы.
          </p>
        </div>
      </div>
    );
  }

  // Update welcome settings
  const updateWelcome = (field, value) => {
    console.log('updateWelcome called:', field, value);
    setConfig(prev => {
      const welcome = { ...(prev.welcome || { enabled: true, text: '', mediaUrl: null, mediaType: null, buttonText: '🚀 Начать опрос' }) };
      welcome[field] = value;
      console.log('New welcome object:', welcome);
      return { ...prev, welcome };
    });
  };

  // Update variant
  const addVariant = () => {
    const key = prompt('Введите start параметр (например: ad_facebook):');
    if (!key || !key.trim()) return;
    const variants = { ...(config.variants || {}) };
    variants[key.trim()] = {
      welcome: { text: '', mediaUrl: null, mediaType: null },
    };
    setConfig({ ...config, variants });
  };

  const removeVariant = (key) => {
    if (!confirm(`Удалить вариант "${key}"?`)) return;
    const variants = { ...(config.variants || {}) };
    delete variants[key];
    setConfig({ ...config, variants });
  };

  const updateVariant = (variantKey, section, field, value) => {
    const variants = { ...(config.variants || {}) };
    variants[variantKey] = { ...(variants[variantKey] || {}) };
    variants[variantKey][section] = { ...(variants[variantKey][section] || {}), [field]: value };
    setConfig({ ...config, variants });
  };

  // Rules management
  const addRule = () => {
    const rules = [...(config.rules || [])];
    rules.push({
      id: `rule_${Date.now()}`,
      name: 'Новое правило',
      conditions: [{ param: 'country', operator: 'eq', value: '' }],
      action: { type: 'skip', questionIds: [] },
    });
    setConfig({ ...config, rules });
  };

  const removeRule = (index) => {
    if (!confirm('Удалить это правило?')) return;
    const rules = [...(config.rules || [])];
    rules.splice(index, 1);
    setConfig({ ...config, rules });
  };

  const updateRule = (index, field, value) => {
    const rules = [...(config.rules || [])];
    rules[index] = { ...rules[index], [field]: value };
    setConfig({ ...config, rules });
  };

  const updateRuleCondition = (ruleIndex, condIndex, field, value) => {
    const rules = [...(config.rules || [])];
    const conditions = [...rules[ruleIndex].conditions];
    conditions[condIndex] = { ...conditions[condIndex], [field]: value };
    rules[ruleIndex] = { ...rules[ruleIndex], conditions };
    setConfig({ ...config, rules });
  };

  const addRuleCondition = (ruleIndex) => {
    const rules = [...(config.rules || [])];
    const conditions = [...rules[ruleIndex].conditions, { param: 'source', operator: 'eq', value: '' }];
    rules[ruleIndex] = { ...rules[ruleIndex], conditions };
    setConfig({ ...config, rules });
  };

  const removeRuleCondition = (ruleIndex, condIndex) => {
    const rules = [...(config.rules || [])];
    const conditions = [...rules[ruleIndex].conditions];
    conditions.splice(condIndex, 1);
    rules[ruleIndex] = { ...rules[ruleIndex], conditions };
    setConfig({ ...config, rules });
  };

  const updateRuleAction = (ruleIndex, field, value) => {
    const rules = [...(config.rules || [])];
    rules[ruleIndex] = { ...rules[ruleIndex], action: { ...rules[ruleIndex].action, [field]: value } };
    setConfig({ ...config, rules });
  };

  return (
    <>
      {/* Tidy Mode Toggle */}
      <div className="card" style={{ marginBottom: '1rem', background: config.tidyMode ? 'linear-gradient(135deg, #e0f2fe 0%, #f0fdf4 100%)' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={config.tidyMode === true}
              onChange={(e) => {
                const newValue = e.target.checked;
                setConfig(prev => ({ ...prev, tidyMode: newValue }));
                handleToggleChange('tidyMode', newValue);
              }}
            />
            <span className="toggle-slider"></span>
          </label>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <strong>🧹 Tidy режим</strong>
              {config.tidyMode && <span style={{ fontSize: '0.75rem', background: '#22c55e', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px' }}>АКТИВЕН</span>}
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: '0.25rem 0 0' }}>
              Образовательный режим для модерации Telegram Ads. Заменяет вопросы на тему финансовой грамотности, пропускает отправку в CRM.
            </p>
          </div>
        </div>
        {config.tidyMode && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
            <strong>📋 В этом режиме:</strong>
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
              <li>7 вопросов о финансовой грамотности (вместо стандартных)</li>
              <li>9 образовательных профилей пользователей</li>
              <li>Рекомендации: Telegram-каналы, книги, калькуляторы</li>
              <li>CRM интеграция отключена</li>
            </ul>
          </div>
        )}
        
        {/* Tidy Mode Language Selector */}
        {config.tidyMode && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <strong>🌐 Язык tidy-режима:</strong>
              <select
                value={config.tidyLanguage || 'ru'}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setConfig(prev => ({ ...prev, tidyLanguage: newValue }));
                  handleToggleChange('tidyLanguage', newValue);
                }}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--gray-300)',
                  background: 'white',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="ru">🇷🇺 Русский</option>
                <option value="en">🇬🇧 English</option>
              </select>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', margin: '0.5rem 0 0' }}>
              {config.tidyLanguage === 'en' 
                ? 'All tidy mode content will be displayed in English'
                : 'Весь контент tidy-режима будет на русском языке'}
            </p>
          </div>
        )}
      </div>

      {/* Scoring Mode Toggle */}
      {!config.tidyMode && (
        <div className="card" style={{ marginBottom: '1rem', background: config.scoringEnabled !== false ? 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={config.scoringEnabled !== false}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setConfig(prev => ({ ...prev, scoringEnabled: newValue }));
                  handleToggleChange('scoringEnabled', newValue);
                }}
              />
              <span className="toggle-slider"></span>
            </label>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <strong>📊 Скоринг архетипов</strong>
                {config.scoringEnabled !== false && <span style={{ fontSize: '0.75rem', background: '#f59e0b', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px' }}>АКТИВЕН</span>}
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: '0.25rem 0 0' }}>
                Расчёт инвестиционного архетипа на основе ответов. Если выключено — опрос без оценки профиля.
              </p>
            </div>
          </div>
          {config.scoringEnabled !== false && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
              <strong>📋 В этом режиме:</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                <li>Каждый ответ имеет баллы для 5 архетипов</li>
                <li>По итогам определяется профиль инвестора</li>
                <li>Архетип показывается в UI и отправляется в CRM</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* English Mode Toggle */}
      {!config.tidyMode && (
        <div className="card" style={{ marginBottom: '1rem', background: config.engMode === true ? 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%)' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={config.engMode === true}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setConfig(prev => ({ ...prev, engMode: newValue }));
                  handleToggleChange('engMode', newValue);
                }}
              />
              <span className="toggle-slider"></span>
            </label>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <strong>🇬🇧 Eng-mode (English UI)</strong>
                {config.engMode === true && <span style={{ fontSize: '0.75rem', background: '#6366f1', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px' }}>ACTIVE</span>}
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: '0.25rem 0 0' }}>
                Display all hardcoded bot messages in English (buttons, system messages, prompts).
              </p>
            </div>
          </div>
          {config.engMode === true && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
              <strong>📋 In this mode:</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                <li>All system messages displayed in English</li>
                <li>Button labels (Share phone, Continue, etc.) in English</li>
                <li>Error messages and help text in English</li>
                <li>Custom texts from config remain unchanged</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* OSINT Lookup Toggle */}
      <div className="card" style={{ marginBottom: '1rem', background: config.osintLookup?.enabled ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={config.osintLookup?.enabled === true}
              onChange={(e) => {
                const newValue = e.target.checked;
                setConfig(prev => ({ ...prev, osintLookup: { ...prev.osintLookup, enabled: newValue } }));
                handleToggleChange('osintLookup', { ...config.osintLookup, enabled: newValue });
              }}
            />
            <span className="toggle-slider"></span>
          </label>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <strong>🔍 OSINT Lookup</strong>
              {config.osintLookup?.enabled && <span style={{ fontSize: '0.75rem', background: '#10b981', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px' }}>АКТИВЕН</span>}
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: '0.25rem 0 0' }}>
              Автоматический поиск данных о новых пользователях через OSINT API (телефон, страна, профили).
            </p>
          </div>
        </div>
        {config.osintLookup?.enabled && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>🔑 API Key</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder={osintSettings.hasApiKey ? osintSettings.apiKeyMasked : 'Введите OSINT API ключ'}
                  value={osintApiKey}
                  onChange={e => setOsintApiKey(e.target.value)}
                  style={{ flex: 1, padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                />
                <button
                  className="btn btn-primary btn-sm"
                  disabled={osintSaving || !osintApiKey.trim()}
                  onClick={async () => {
                    setOsintSaving(true);
                    try {
                      const result = await api.saveOsintSettings({ apiKey: osintApiKey.trim(), enabled: true });
                      setOsintSettings(result);
                      setOsintApiKey('');
                      alert('✅ API ключ сохранён!');
                    } catch (e) {
                      alert('❌ Ошибка: ' + e.message);
                    } finally {
                      setOsintSaving(false);
                    }
                  }}
                >
                  {osintSaving ? '...' : '💾'}
                </button>
              </div>
              {osintSettings.hasApiKey && (
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  Текущий ключ: {osintSettings.apiKeyMasked}
                  {osintSettings.updatedAt && ` (обновлён ${new Date(osintSettings.updatedAt).toLocaleDateString('ru')})`}
                </div>
              )}
              {!osintSettings.hasApiKey && (
                <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                  ⚠️ API ключ не настроен — lookup работать не будет
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              <strong>📋 В этом режиме:</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                <li>При /start ставится в очередь OSINT lookup</li>
                <li>Cron каждые 5 мин обрабатывает очередь</li>
                <li>Данные: телефон, страна, профили, дата регистрации</li>
                <li>Если нет телефона — авто-заполняется из OSINT</li>
                <li>Данные видны в карточке пользователя и уведомлении в группу</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {CONFIG_SECTIONS.map(s => (
          <button
            key={s.id}
            className={`btn ${currentSection === s.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onSectionChange?.(s.id)}
          >
            {s.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={handleReset} disabled={saving}>
            🔄 Сбросить
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '💾 Сохранение...' : '💾 Сохранить'}
          </button>
        </div>
      </div>

      {/* Welcome Section */}
      {currentSection === 'welcome' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Приветственное сообщение</h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={config.welcome?.enabled !== false}
                onChange={(e) => updateWelcome('enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Сообщение отправляется пользователю при команде /start. Если выключено — сразу начнётся опрос.
          </p>

          {config.welcome?.enabled !== false && (
            <>
              <MediaUploader
                label="Медиа (текст будет подписью к медиа):"
                mediaUrl={config.welcome?.mediaUrl}
                mediaType={config.welcome?.mediaType}
                onChangeUrl={(url) => updateWelcome('mediaUrl', url)}
                onChangeType={(type) => updateWelcome('mediaType', type)}
              />

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                  Текст сообщения {config.welcome?.mediaUrl ? '(caption к медиа)' : ''}:
                </label>
                <textarea
                  value={config.welcome?.text || ''}
                  onChange={(e) => updateWelcome('text', e.target.value)}
                  rows={4}
                  className="input"
                  style={{ width: '100%' }}
                  placeholder="Текст приветствия (поддерживает HTML)"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Текст кнопки:</label>
                <input
                  type="text"
                  value={config.welcome?.buttonText || '🚀 Начать опрос'}
                  onChange={(e) => updateWelcome('buttonText', e.target.value)}
                  className="input"
                  style={{ width: '100%' }}
                  placeholder="Текст на кнопке начала опроса"
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Questions Section */}
      {currentSection === 'questions' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Вопросы опроса ({(config.questions || []).length})</h3>
            <button className="btn btn-primary btn-sm" onClick={addQuestion}>+ Добавить вопрос</button>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Медиа отправляется с текстом вопроса как подпись (caption).
          </p>

          {/* Question delivery mode */}
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>📝 Режим показа вопросов</label>
            <select
              value={config.questionMode || 'new_message'}
              onChange={(e) => setConfig({ ...config, questionMode: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
            >
              <option value="new_message">Новое сообщение — каждый вопрос отдельным сообщением</option>
              <option value="edit_message">Редактирование — вопрос заменяется в том же сообщении</option>
            </select>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
              В режиме «Редактирование» бот редактирует предыдущее сообщение вместо отправки нового. Чат остаётся чистым. При смене типа (текст↔медиа) вопрос отправляется отдельно.
            </p>
          </div>

          {(config.questions || []).map((q, qIndex) => (
            <div key={q.id} style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <strong>Вопрос {qIndex + 1}</strong>
                <button className="btn btn-danger btn-sm" onClick={() => removeQuestion(qIndex)}>✕</button>
              </div>

              {/* Media + Caption (question text) */}
              <MediaUploader
                label="Медиа (текст вопроса будет подписью):"
                mediaUrl={q.mediaUrl}
                mediaType={q.mediaType}
                onChangeUrl={(url) => updateQuestion(qIndex, 'mediaUrl', url)}
                onChangeType={(type) => updateQuestion(qIndex, 'mediaType', type)}
                compact
              />

              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                  Текст вопроса {q.mediaUrl ? '(caption к медиа)' : ''}:
                </label>
                <textarea
                  value={q.text}
                  onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                  placeholder="Текст вопроса (поддерживает HTML: <b>жирный</b>)"
                />
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Варианты ответов:</span>
                  <button className="btn btn-secondary btn-sm" onClick={() => addOption(qIndex)}>+ Вариант</button>
                </div>
                {(q.options || []).map((opt, oIndex) => (
                  <div key={opt.id} style={{ marginBottom: '0.75rem', padding: '0.5rem', background: 'white', borderRadius: '4px', border: '1px solid var(--gray-200)' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: config.scoringEnabled !== false ? '0.5rem' : 0 }}>
                      <input
                        type="text"
                        value={opt.text}
                        onChange={(e) => updateQuestionOption(qIndex, oIndex, 'text', e.target.value)}
                        style={{ flex: 1, padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                        placeholder="Текст варианта"
                      />
                      <button className="btn btn-danger btn-sm" onClick={() => removeOption(qIndex, oIndex)} style={{ padding: '0.25rem 0.5rem' }}>✕</button>
                    </div>
                    {/* Score editor - only show when scoring is enabled */}
                    {config.scoringEnabled !== false && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginRight: '0.25rem' }}>Score:</span>
                        {['conservative', 'balanced', 'growth', 'aggressive', 'analyst'].map(archetype => (
                          <div key={archetype} style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }}>
                            <span style={{ fontSize: '0.625rem', color: 'var(--gray-400)' }}>{archetype.slice(0, 3)}:</span>
                            <input
                              type="number"
                              value={opt.score?.[archetype] || 0}
                              onChange={(e) => {
                                const newScore = { ...opt.score, [archetype]: parseInt(e.target.value) || 0 };
                                // Remove zero values to keep config clean
                                Object.keys(newScore).forEach(k => { if (newScore[k] === 0) delete newScore[k]; });
                                updateQuestionOption(qIndex, oIndex, 'score', newScore);
                              }}
                              style={{ width: '40px', padding: '0.125rem 0.25rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.75rem', textAlign: 'center' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {(!config.questions || config.questions.length === 0) && (
            <p style={{ color: 'var(--gray-500)', padding: '1rem' }}>Нет вопросов. Нажмите "Добавить вопрос" чтобы создать.</p>
          )}
        </div>
      )}

      {/* Notifications Section */}
      {currentSection === 'notifications' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Уведомления</h3>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Медиа отправляется с текстом как подпись (caption). Используйте <code>{'{archetype}'}</code> для подстановки результата.
          </p>

          {/* Basic notifications */}
          <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>📨 Базовые сообщения</h4>
          {Object.entries(config.notifications || {}).filter(([key]) => !['prePhoneMessage', 'phoneRequest', 'nameRequest', 'loadingMessages'].includes(key)).map(([key, notif]) => (
            <div key={key} style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong>
                  {key === 'surveyComplete' ? '🎉 Завершение опроса' : key === 'thankYou' ? '✅ Благодарность' : key}
                </strong>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notif.enabled !== false}
                    onChange={(e) => updateNotification(key, 'enabled', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notif.enabled !== false ? (
                <>
                  <MediaUploader
                    label="Медиа (текст будет подписью):"
                    mediaUrl={notif.mediaUrl}
                    mediaType={notif.mediaType}
                    onChangeUrl={(url) => updateNotification(key, 'mediaUrl', url)}
                    onChangeType={(type) => updateNotification(key, 'mediaType', type)}
                  />
                  <div style={{ marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                      Текст {notif.mediaUrl ? '(caption к медиа)' : 'сообщения'}:
                    </label>
                    <textarea
                      value={notif.text || ''}
                      onChange={(e) => updateNotification(key, 'text', e.target.value)}
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                      placeholder="Текст сообщения"
                    />
                  </div>
                  {/* Button configuration */}
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>🔘 Кнопка «Далее»</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={notif.button?.enabled === true}
                          disabled={notif.phoneButton?.enabled === true}
                          onChange={(e) => {
                            const notifications = { ...(config.notifications || {}) };
                            notifications[key] = { ...(notifications[key] || {}), button: { ...(notifications[key]?.button || { text: '➡️ Далее' }), enabled: e.target.checked } };
                            setConfig({ ...config, notifications });
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                      Пользователь нажимает кнопку для продолжения. Без кнопки — автоматический переход.
                    </p>
                    {notif.button?.enabled && !notif.phoneButton?.enabled && (
                      <input
                        type="text"
                        value={notif.button?.text || ''}
                        onChange={(e) => {
                          const notifications = { ...(config.notifications || {}) };
                          notifications[key] = { ...(notifications[key] || {}), button: { ...(notifications[key]?.button || {}), text: e.target.value } };
                          setConfig({ ...config, notifications });
                        }}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                        placeholder="Текст кнопки (например: ➡️ Далее)"
                      />
                    )}
                  </div>
                  {/* Phone button configuration — only for notifications before phone collection */}
                  {key !== 'thankYou' && (
                    <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>📱 Кнопка «Поделиться номером»</span>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={notif.phoneButton?.enabled === true}
                            onChange={(e) => {
                              const notifications = { ...(config.notifications || {}) };
                              const updated = { ...(notifications[key] || {}), phoneButton: { ...(notifications[key]?.phoneButton || { text: '📱 Поделиться номером' }), enabled: e.target.checked } };
                              if (e.target.checked) {
                                updated.button = { ...(updated.button || {}), enabled: false };
                              }
                              notifications[key] = updated;
                              setConfig({ ...config, notifications });
                            }}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                        Стандартный запрос номера через Telegram API. Не совместимо с кнопкой «Далее».
                      </p>
                      {notif.phoneButton?.enabled && (
                        <input
                          type="text"
                          value={notif.phoneButton?.text || ''}
                          onChange={(e) => {
                            const notifications = { ...(config.notifications || {}) };
                            notifications[key] = { ...(notifications[key] || {}), phoneButton: { ...(notifications[key]?.phoneButton || {}), text: e.target.value } };
                            setConfig({ ...config, notifications });
                          }}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                          placeholder="Текст кнопки (например: 📱 Поделиться номером)"
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  Отключено — сообщение не будет отправляться.
                </p>
              )}
            </div>
          ))}

          {/* Loading Messages Section */}
          <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem' }}>⏳ Загрузочные сообщения</h4>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Последовательные сообщения после завершения опроса, перед запросом контактных данных.
          </p>
          
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>⏳ Включить загрузочные сообщения</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.notifications?.loadingMessages?.enabled === true}
                  onChange={(e) => {
                    const notifications = { ...(config.notifications || {}) };
                    notifications.loadingMessages = { ...(notifications.loadingMessages || {}), enabled: e.target.checked };
                    if (!notifications.loadingMessages.messages) {
                      notifications.loadingMessages.messages = [
                        { emoji: '⏳', text: 'Мы анализируем Ваши ответы...', delaySec: 3 },
                        { emoji: '🔍', text: 'Ищем инструменты и подходы под Вашу ситуацию...', delaySec: 3 },
                        { emoji: '👨‍💼', text: 'Назначаем для Вас финансового консультанта...', delaySec: 3 },
                      ];
                    }
                    setConfig({ ...config, notifications });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              После опроса показываются сообщения с задержкой, имитируя анализ результатов.
            </p>

            {config.notifications?.loadingMessages?.enabled && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Режим отправки</label>
                  <select
                    value={config.notifications?.loadingMessages?.mode || 'sequential'}
                    onChange={(e) => {
                      const notifications = { ...(config.notifications || {}) };
                      notifications.loadingMessages = { ...(notifications.loadingMessages || {}), mode: e.target.value };
                      setConfig({ ...config, notifications });
                    }}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                  >
                    <option value="sequential">Последовательные — каждое сообщение отдельно</option>
                    <option value="draft">Черновик — одно сообщение, текст обновляется</option>
                  </select>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <MediaUploader
                    label={`Общее медиа для всех сообщений${config.notifications?.loadingMessages?.mediaUrl ? ' (текст станет подписью)' : ''}:`}
                    mediaUrl={config.notifications?.loadingMessages?.mediaUrl}
                    mediaType={config.notifications?.loadingMessages?.mediaType}
                    onChangeUrl={(url) => {
                      setConfig(prev => {
                        const notifications = { ...(prev.notifications || {}) };
                        notifications.loadingMessages = { ...(notifications.loadingMessages || {}), mediaUrl: url };
                        return { ...prev, notifications };
                      });
                    }}
                    onChangeType={(type) => {
                      setConfig(prev => {
                        const notifications = { ...(prev.notifications || {}) };
                        notifications.loadingMessages = { ...(notifications.loadingMessages || {}), mediaType: type };
                        return { ...prev, notifications };
                      });
                    }}
                    compact
                  />
                  <p style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
                    Один медиа-файл для всех загрузочных сообщений. При смене текста медиа не заменяется.
                  </p>
                </div>
              </>
            )}

            {config.notifications?.loadingMessages?.enabled && (
              <>
                {(config.notifications?.loadingMessages?.messages || []).map((msg, idx) => (
                  <div key={idx} style={{ padding: '0.75rem', marginBottom: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Сообщение {idx + 1}</span>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                        onClick={() => {
                          const notifications = { ...(config.notifications || {}) };
                          const messages = [...(notifications.loadingMessages?.messages || [])];
                          messages.splice(idx, 1);
                          notifications.loadingMessages = { ...notifications.loadingMessages, messages };
                          setConfig({ ...config, notifications });
                        }}
                      >✕</button>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ flex: '0 0 60px' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Эмодзи</label>
                        <input
                          type="text"
                          value={msg.emoji || ''}
                          onChange={(e) => {
                            setConfig(prev => {
                              const notifications = { ...(prev.notifications || {}) };
                              const messages = [...(notifications.loadingMessages?.messages || [])];
                              messages[idx] = { ...messages[idx], emoji: e.target.value };
                              notifications.loadingMessages = { ...notifications.loadingMessages, messages };
                              return { ...prev, notifications };
                            });
                          }}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', textAlign: 'center', fontSize: '1.25rem' }}
                          placeholder="⏳"
                        />
                      </div>
                      <div style={{ flex: '0 0 80px' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Задержка (сек)</label>
                        <input
                          type="number"
                          min="1"
                          value={msg.delaySec || 3}
                          onChange={(e) => {
                            setConfig(prev => {
                              const notifications = { ...(prev.notifications || {}) };
                              const messages = [...(notifications.loadingMessages?.messages || [])];
                              messages[idx] = { ...messages[idx], delaySec: parseInt(e.target.value) || 3 };
                              notifications.loadingMessages = { ...notifications.loadingMessages, messages };
                              return { ...prev, notifications };
                            });
                          }}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', textAlign: 'center' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Текст</label>
                        <textarea
                          value={msg.text || ''}
                          onChange={(e) => {
                            setConfig(prev => {
                              const notifications = { ...(prev.notifications || {}) };
                              const messages = [...(notifications.loadingMessages?.messages || [])];
                              messages[idx] = { ...messages[idx], text: e.target.value };
                              notifications.loadingMessages = { ...notifications.loadingMessages, messages };
                              return { ...prev, notifications };
                            });
                          }}
                          rows={2}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', resize: 'vertical' }}
                          placeholder="Текст сообщения..."
                        />
                      </div>
                    </div>
                    <MediaUploader
                      label={msg.mediaUrl ? 'Своё медиа (переопределяет общее):' : 'Своё медиа (переопределить общее):'}
                      mediaUrl={msg.mediaUrl}
                      mediaType={msg.mediaType}
                      onChangeUrl={(url) => {
                        setConfig(prev => {
                          const notifications = { ...(prev.notifications || {}) };
                          const messages = [...(notifications.loadingMessages?.messages || [])];
                          messages[idx] = { ...messages[idx], mediaUrl: url };
                          notifications.loadingMessages = { ...notifications.loadingMessages, messages };
                          return { ...prev, notifications };
                        });
                      }}
                      onChangeType={(type) => {
                        setConfig(prev => {
                          const notifications = { ...(prev.notifications || {}) };
                          const messages = [...(notifications.loadingMessages?.messages || [])];
                          messages[idx] = { ...messages[idx], mediaType: type };
                          notifications.loadingMessages = { ...notifications.loadingMessages, messages };
                          return { ...prev, notifications };
                        });
                      }}
                      compact
                    />
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => {
                    const notifications = { ...(config.notifications || {}) };
                    const messages = [...(notifications.loadingMessages?.messages || [])];
                    messages.push({ emoji: '⏳', text: '', delaySec: 3 });
                    notifications.loadingMessages = { ...notifications.loadingMessages, messages };
                    setConfig({ ...config, notifications });
                  }}
                >
                  + Добавить сообщение
                </button>
              </>
            )}
          </div>

          {/* Pre-Phone Message (before phone request) */}
          <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem' }}>📲 Запрос телефона</h4>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Сообщение перед запросом телефона и настройки кнопки.
          </p>
          
          {/* Pre-phone message toggle */}
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>💬 Сообщение до запроса телефона</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.notifications?.prePhoneMessage?.enabled === true}
                  onChange={(e) => {
                    const notifications = { ...(config.notifications || {}) };
                    notifications.prePhoneMessage = { ...(notifications.prePhoneMessage || {}), enabled: e.target.checked };
                    setConfig({ ...config, notifications });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Отправляется после завершения опроса, перед кнопкой "Поделиться номером".
            </p>

            {config.notifications?.prePhoneMessage?.enabled && (
              <>
                <MediaUploader
                  label="Медиа:"
                  mediaUrl={config.notifications?.prePhoneMessage?.mediaUrl}
                  mediaType={config.notifications?.prePhoneMessage?.mediaType}
                  onChangeUrl={(url) => updateNotification('prePhoneMessage', 'mediaUrl', url)}
                  onChangeType={(type) => updateNotification('prePhoneMessage', 'mediaType', type)}
                />
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Текст сообщения:
                  </label>
                  <textarea
                    value={config.notifications?.prePhoneMessage?.text || ''}
                    onChange={(e) => updateNotification('prePhoneMessage', 'text', e.target.value)}
                    rows={3}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                    placeholder="✅ Спасибо! Мы определили, какие стратегии подойдут именно Вам..."
                  />
                </div>
                {/* Button configuration for prePhoneMessage */}
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>🔘 Кнопка «Далее»</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={config.notifications?.prePhoneMessage?.button?.enabled === true}
                        disabled={config.notifications?.prePhoneMessage?.phoneButton?.enabled === true}
                        onChange={(e) => {
                          const notifications = { ...(config.notifications || {}) };
                          notifications.prePhoneMessage = { ...(notifications.prePhoneMessage || {}), button: { ...(notifications.prePhoneMessage?.button || { text: '➡️ Далее' }), enabled: e.target.checked } };
                          setConfig({ ...config, notifications });
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                    Пользователь нажимает кнопку для продолжения к запросу телефона.
                  </p>
                  {config.notifications?.prePhoneMessage?.button?.enabled && !config.notifications?.prePhoneMessage?.phoneButton?.enabled && (
                    <input
                      type="text"
                      value={config.notifications?.prePhoneMessage?.button?.text || ''}
                      onChange={(e) => {
                        const notifications = { ...(config.notifications || {}) };
                        notifications.prePhoneMessage = { ...(notifications.prePhoneMessage || {}), button: { ...(notifications.prePhoneMessage?.button || {}), text: e.target.value } };
                        setConfig({ ...config, notifications });
                      }}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                      placeholder="Текст кнопки (например: ➡️ Далее)"
                    />
                  )}
                </div>
                {/* Phone button for prePhoneMessage */}
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>📱 Кнопка «Поделиться номером»</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={config.notifications?.prePhoneMessage?.phoneButton?.enabled === true}
                        onChange={(e) => {
                          const notifications = { ...(config.notifications || {}) };
                          const updated = { ...(notifications.prePhoneMessage || {}), phoneButton: { ...(notifications.prePhoneMessage?.phoneButton || { text: '📱 Поделиться номером' }), enabled: e.target.checked } };
                          if (e.target.checked) {
                            updated.button = { ...(updated.button || {}), enabled: false };
                          }
                          notifications.prePhoneMessage = updated;
                          setConfig({ ...config, notifications });
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                    Стандартный запрос номера через Telegram API. Не совместимо с кнопкой «Далее».
                  </p>
                  {config.notifications?.prePhoneMessage?.phoneButton?.enabled && (
                    <input
                      type="text"
                      value={config.notifications?.prePhoneMessage?.phoneButton?.text || ''}
                      onChange={(e) => {
                        const notifications = { ...(config.notifications || {}) };
                        notifications.prePhoneMessage = { ...(notifications.prePhoneMessage || {}), phoneButton: { ...(notifications.prePhoneMessage?.phoneButton || {}), text: e.target.value } };
                        setConfig({ ...config, notifications });
                      }}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                      placeholder="Текст кнопки (например: 📱 Поделиться номером)"
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Name Request Settings */}
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>👤 Запрос имени</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.notifications?.nameRequest?.enabled === true}
                  onChange={(e) => {
                    const notifications = { ...(config.notifications || {}) };
                    notifications.nameRequest = { ...(notifications.nameRequest || {}), enabled: e.target.checked };
                    setConfig({ ...config, notifications });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Запросить имя пользователя перед номером телефона.
            </p>

            {config.notifications?.nameRequest?.enabled && (
              <>
                <MediaUploader
                  label="Медиа:"
                  mediaUrl={config.notifications?.nameRequest?.mediaUrl}
                  mediaType={config.notifications?.nameRequest?.mediaType}
                  onChangeUrl={(url) => updateNotification('nameRequest', 'mediaUrl', url)}
                  onChangeType={(type) => updateNotification('nameRequest', 'mediaType', type)}
                />
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Текст запроса:
                  </label>
                  <textarea
                    value={config.notifications?.nameRequest?.text || ''}
                    onChange={(e) => updateNotification('nameRequest', 'text', e.target.value)}
                    rows={2}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                    placeholder="👤 Введите ваше Имя, чтобы получить доступ к заработку и консультацию от куратора:"
                  />
                </div>
                {/* Button configuration for nameRequest */}
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>🔘 Кнопка «Далее»</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={config.notifications?.nameRequest?.button?.enabled === true}
                        disabled={config.notifications?.nameRequest?.phoneButton?.enabled === true}
                        onChange={(e) => {
                          const notifications = { ...(config.notifications || {}) };
                          notifications.nameRequest = { ...(notifications.nameRequest || {}), button: { ...(notifications.nameRequest?.button || { text: '➡️ Далее' }), enabled: e.target.checked } };
                          setConfig({ ...config, notifications });
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                    Кнопка вместо ожидания ввода имени — пропускает запрос имени.
                  </p>
                  {config.notifications?.nameRequest?.button?.enabled && !config.notifications?.nameRequest?.phoneButton?.enabled && (
                    <input
                      type="text"
                      value={config.notifications?.nameRequest?.button?.text || ''}
                      onChange={(e) => {
                        const notifications = { ...(config.notifications || {}) };
                        notifications.nameRequest = { ...(notifications.nameRequest || {}), button: { ...(notifications.nameRequest?.button || {}), text: e.target.value } };
                        setConfig({ ...config, notifications });
                      }}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                      placeholder="Текст кнопки (например: ➡️ Далее)"
                    />
                  )}
                </div>
                {/* Phone button for nameRequest */}
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>📱 Кнопка «Поделиться номером»</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={config.notifications?.nameRequest?.phoneButton?.enabled === true}
                        onChange={(e) => {
                          const notifications = { ...(config.notifications || {}) };
                          const updated = { ...(notifications.nameRequest || {}), phoneButton: { ...(notifications.nameRequest?.phoneButton || { text: '📱 Поделиться номером' }), enabled: e.target.checked } };
                          if (e.target.checked) {
                            updated.button = { ...(updated.button || {}), enabled: false };
                          }
                          notifications.nameRequest = updated;
                          setConfig({ ...config, notifications });
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                    Стандартный запрос номера через Telegram API. Не совместимо с кнопкой «Далее».
                  </p>
                  {config.notifications?.nameRequest?.phoneButton?.enabled && (
                    <input
                      type="text"
                      value={config.notifications?.nameRequest?.phoneButton?.text || ''}
                      onChange={(e) => {
                        const notifications = { ...(config.notifications || {}) };
                        notifications.nameRequest = { ...(notifications.nameRequest || {}), phoneButton: { ...(notifications.nameRequest?.phoneButton || {}), text: e.target.value } };
                        setConfig({ ...config, notifications });
                      }}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                      placeholder="Текст кнопки (например: 📱 Поделиться номером)"
                    />
                  )}
                </div>
              </>
            )}
          </div>
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
            <strong style={{ display: 'block', marginBottom: '0.75rem' }}>📱 Запрос номера телефона</strong>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.notifications?.phoneRequest?.allowManualInput !== false}
                  onChange={(e) => {
                    const notifications = { ...(config.notifications || {}) };
                    notifications.phoneRequest = { ...(notifications.phoneRequest || {}), allowManualInput: e.target.checked };
                    setConfig({ ...config, notifications });
                  }}
                />
                <span style={{ fontSize: '0.875rem' }}>Разрешить ручной ввод номера</span>
              </label>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginLeft: '1.5rem' }}>
                Пользователь сможет ввести номер вручную (+7XXXXXXXXXX)
              </p>
            </div>
            <MediaUploader
              label="Медиа:"
              mediaUrl={config.notifications?.phoneRequest?.mediaUrl}
              mediaType={config.notifications?.phoneRequest?.mediaType}
              onChangeUrl={(url) => updateNotification('phoneRequest', 'mediaUrl', url)}
              onChangeType={(type) => updateNotification('phoneRequest', 'mediaType', type)}
            />
            <div style={{ marginTop: '0.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                Текст запроса {config.notifications?.phoneRequest?.mediaUrl ? '(caption к медиа)' : ''}:
              </label>
              <textarea
                value={config.notifications?.phoneRequest?.text || ''}
                onChange={(e) => updateNotification('phoneRequest', 'text', e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                placeholder="📱 Чтобы получить доступ, нажмите кнопку ниже или введите номер вручную (+7XXXXXXXXXX)"
              />
            </div>
          </div>
          <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem' }}>⏰ Отложенные уведомления</h4>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Автоматические напоминания отправляются по расписанию. Задержки указываются в часах.
          </p>

          {/* Incomplete Survey Reminders */}
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '2px solid var(--warning)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>🔔 Незавершённый опрос</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.scheduledNotifications?.incompleteSurvey?.enabled !== false}
                  onChange={(e) => {
                    const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                    scheduledNotifications.incompleteSurvey = { ...(scheduledNotifications.incompleteSurvey || {}), enabled: e.target.checked };
                    setConfig({ ...config, scheduledNotifications });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Напоминания для пользователей, которые начали, но не завершили опрос.
            </p>

            {config.scheduledNotifications?.incompleteSurvey?.enabled !== false && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Задержки (минуты через запятую):
                  </label>
                  <input
                    type="text"
                    value={(config.scheduledNotifications?.incompleteSurvey?.delays || [60, 120, 180]).join(', ')}
                    onChange={(e) => {
                      const delays = e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                      const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                      scheduledNotifications.incompleteSurvey = { ...(scheduledNotifications.incompleteSurvey || {}), delays };
                      setConfig({ ...config, scheduledNotifications });
                    }}
                    className="input"
                    style={{ width: '100%' }}
                    placeholder="60, 120, 180"
                  />
                </div>

                {(config.scheduledNotifications?.incompleteSurvey?.messages || []).map((msg, idx) => (
                  <div key={idx} style={{ padding: '0.75rem', marginBottom: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Сообщение {idx + 1}</span>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                        onClick={() => {
                          const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                          const messages = [...(scheduledNotifications.incompleteSurvey?.messages || [])];
                          messages.splice(idx, 1);
                          scheduledNotifications.incompleteSurvey = { ...(scheduledNotifications.incompleteSurvey || {}), messages };
                          setConfig({ ...config, scheduledNotifications });
                        }}
                      >✕</button>
                    </div>
                    <textarea
                      value={msg.text || ''}
                      onChange={(e) => {
                        const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                        const messages = [...(scheduledNotifications.incompleteSurvey?.messages || [])];
                        messages[idx] = { ...messages[idx], text: e.target.value };
                        scheduledNotifications.incompleteSurvey = { ...(scheduledNotifications.incompleteSurvey || {}), messages };
                        setConfig({ ...config, scheduledNotifications });
                      }}
                      rows={2}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                      placeholder="Текст напоминания"
                    />
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                    const messages = [...(scheduledNotifications.incompleteSurvey?.messages || [])];
                    messages.push({ text: '', mediaUrl: null, mediaType: null });
                    scheduledNotifications.incompleteSurvey = { ...(scheduledNotifications.incompleteSurvey || {}), messages };
                    setConfig({ ...config, scheduledNotifications });
                  }}
                >+ Добавить сообщение</button>
              </>
            )}
          </div>

          {/* Completed No Name Reminders */}
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '2px solid var(--warning)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>✍️ Завершил без имени</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.scheduledNotifications?.completedNoName?.enabled !== false}
                  onChange={(e) => {
                    const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                    scheduledNotifications.completedNoName = { ...(scheduledNotifications.completedNoName || {}), enabled: e.target.checked };
                    setConfig({ ...config, scheduledNotifications });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Напоминания для пользователей, которые завершили опрос, но не ввели имя. Напоминания о телефоне отправляются только после ввода имени.
            </p>

            {config.scheduledNotifications?.completedNoName?.enabled !== false && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Задержки (минуты через запятую):
                  </label>
                  <input
                    type="text"
                    value={(config.scheduledNotifications?.completedNoName?.delays || [5, 30, 120]).join(', ')}
                    onChange={(e) => {
                      const delays = e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                      const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                      scheduledNotifications.completedNoName = { ...(scheduledNotifications.completedNoName || {}), delays };
                      setConfig({ ...config, scheduledNotifications });
                    }}
                    className="input"
                    style={{ width: '100%' }}
                    placeholder="5, 30, 120"
                  />
                </div>

                {(config.scheduledNotifications?.completedNoName?.messages || []).map((msg, idx) => (
                  <div key={idx} style={{ padding: '0.75rem', marginBottom: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Сообщение {idx + 1}</span>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                        onClick={() => {
                          const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                          const messages = [...(scheduledNotifications.completedNoName?.messages || [])];
                          messages.splice(idx, 1);
                          scheduledNotifications.completedNoName = { ...(scheduledNotifications.completedNoName || {}), messages };
                          setConfig({ ...config, scheduledNotifications });
                        }}
                      >✕</button>
                    </div>
                    <textarea
                      value={msg.text || ''}
                      onChange={(e) => {
                        const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                        const messages = [...(scheduledNotifications.completedNoName?.messages || [])];
                        messages[idx] = { ...messages[idx], text: e.target.value };
                        scheduledNotifications.completedNoName = { ...(scheduledNotifications.completedNoName || {}), messages };
                        setConfig({ ...config, scheduledNotifications });
                      }}
                      rows={2}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                      placeholder="Текст напоминания (введите имя)"
                    />
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                    const messages = [...(scheduledNotifications.completedNoName?.messages || [])];
                    messages.push({ text: '', mediaUrl: null, mediaType: null });
                    scheduledNotifications.completedNoName = { ...(scheduledNotifications.completedNoName || {}), messages };
                    setConfig({ ...config, scheduledNotifications });
                  }}
                >+ Добавить сообщение</button>
              </>
            )}
          </div>

          {/* Completed No Phone Reminders */}
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '2px solid var(--success)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>📱 Завершил без телефона</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.scheduledNotifications?.completedNoPhone?.enabled !== false}
                  onChange={(e) => {
                    const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                    scheduledNotifications.completedNoPhone = { ...(scheduledNotifications.completedNoPhone || {}), enabled: e.target.checked };
                    setConfig({ ...config, scheduledNotifications });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Напоминания для пользователей, которые завершили опрос и ввели имя, но не оставили номер телефона.
            </p>

            {config.scheduledNotifications?.completedNoPhone?.enabled !== false && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Задержки (минуты через запятую):
                  </label>
                  <input
                    type="text"
                    value={(config.scheduledNotifications?.completedNoPhone?.delays || [1, 24, 72]).join(', ')}
                    onChange={(e) => {
                      const delays = e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                      const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                      scheduledNotifications.completedNoPhone = { ...(scheduledNotifications.completedNoPhone || {}), delays };
                      setConfig({ ...config, scheduledNotifications });
                    }}
                    className="input"
                    style={{ width: '100%' }}
                    placeholder="1, 24, 72"
                  />
                </div>

                {(config.scheduledNotifications?.completedNoPhone?.messages || []).map((msg, idx) => (
                  <div key={idx} style={{ padding: '0.75rem', marginBottom: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Сообщение {idx + 1}</span>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                        onClick={() => {
                          const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                          const messages = [...(scheduledNotifications.completedNoPhone?.messages || [])];
                          messages.splice(idx, 1);
                          scheduledNotifications.completedNoPhone = { ...(scheduledNotifications.completedNoPhone || {}), messages };
                          setConfig({ ...config, scheduledNotifications });
                        }}
                      >✕</button>
                    </div>
                    <textarea
                      value={msg.text || ''}
                      onChange={(e) => {
                        const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                        const messages = [...(scheduledNotifications.completedNoPhone?.messages || [])];
                        messages[idx] = { ...messages[idx], text: e.target.value };
                        scheduledNotifications.completedNoPhone = { ...(scheduledNotifications.completedNoPhone || {}), messages };
                        setConfig({ ...config, scheduledNotifications });
                      }}
                      rows={2}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                      placeholder="Текст напоминания"
                    />
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const scheduledNotifications = { ...(config.scheduledNotifications || {}) };
                    const messages = [...(scheduledNotifications.completedNoPhone?.messages || [])];
                    messages.push({ text: '', mediaUrl: null, mediaType: null });
                    scheduledNotifications.completedNoPhone = { ...(scheduledNotifications.completedNoPhone || {}), messages };
                    setConfig({ ...config, scheduledNotifications });
                  }}
                >+ Добавить сообщение</button>
              </>
            )}
          </div>

          {/* New User Notification to Group */}
          <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem' }}>👥 Уведомление о новом пользователе в группу</h4>
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '2px solid var(--primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>🔔 Уведомление в Telegram группу</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.newUserNotification?.enabled === true}
                  onChange={(e) => {
                    const newUserNotification = { ...(config.newUserNotification || { groupId: null, delayMinutes: 5 }), enabled: e.target.checked };
                    setConfig({ ...config, newUserNotification });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Бот отправит уведомление в указанную группу через заданное время после /start. 
              Если пользователь поделился номером телефона — он будет указан в сообщении.
              Кнопка «Написать пользователю» откроет чат с пользователем.
            </p>

            {config.newUserNotification?.enabled && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    ID группы (Telegram Chat ID):
                  </label>
                  <input
                    type="text"
                    value={config.newUserNotification?.groupId || ''}
                    onChange={(e) => {
                      const newUserNotification = { ...(config.newUserNotification || {}), groupId: e.target.value || null };
                      setConfig({ ...config, newUserNotification });
                    }}
                    className="input"
                    style={{ width: '100%', fontFamily: 'monospace' }}
                    placeholder="-1001234567890"
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
                    Добавьте бота в группу и отправьте туда сообщение, затем получите ID через @userinfobot или @getidsbot
                  </p>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Задержка отправки (минуты):
                  </label>
                  <input
                    type="number"
                    value={config.newUserNotification?.delayMinutes || 5}
                    onChange={(e) => {
                      const delayMinutes = parseInt(e.target.value) || 5;
                      const newUserNotification = { ...(config.newUserNotification || {}), delayMinutes };
                      setConfig({ ...config, newUserNotification });
                    }}
                    className="input"
                    style={{ width: '100px' }}
                    min="1"
                    max="60"
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
                    Ждём указанное время после /start, затем отправляем уведомление
                  </p>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.newUserNotification?.onlyAfterSurveyComplete === true}
                      onChange={(e) => {
                        const newUserNotification = { ...(config.newUserNotification || {}), onlyAfterSurveyComplete: e.target.checked };
                        setConfig({ ...config, newUserNotification });
                      }}
                    />
                    <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Только после завершения опроса</span>
                  </label>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
                    Уведомление в группу и экспорт в CRM произойдут только когда пользователь ответит на ВСЕ вопросы опроса.
                    Привязка к последнему вопросу динамическая — работает при любом количестве вопросов.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Daily Stats Report to Group */}
          <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem' }}>📊 Ежедневный отчёт в группу</h4>
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '2px solid var(--primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>📈 Ежедневная статистика</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.dailyStats?.enabled === true}
                  onChange={(e) => {
                    const dailyStats = { ...(config.dailyStats || { groupId: null }), enabled: e.target.checked };
                    setConfig({ ...config, dailyStats });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Каждый день в 10:00 по Киеву бот отправит в группу отчёт за вчерашние сутки: 
              новые пользователи, завершившие опрос, с телефоном/без, география, источники трафика.
              Также показывается сравнение с позавчерашним днём (↑↓).
            </p>

            {config.dailyStats?.enabled && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    ID группы (Telegram Chat ID):
                  </label>
                  <input
                    type="text"
                    value={config.dailyStats?.groupId || ''}
                    onChange={(e) => {
                      const dailyStats = { ...(config.dailyStats || {}), groupId: e.target.value || null };
                      setConfig({ ...config, dailyStats });
                    }}
                    className="input"
                    style={{ width: '100%', fontFamily: 'monospace' }}
                    placeholder="-1001234567890"
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
                    Добавьте бота в группу и отправьте туда сообщение, затем получите ID через @userinfobot или @getidsbot
                  </p>
                </div>
                <button
                  className="btn btn-secondary"
                  style={{ marginTop: '0.5rem' }}
                  onClick={async () => {
                    if (!config.dailyStats?.groupId) {
                      alert('Сначала укажите ID группы');
                      return;
                    }
                    try {
                      await api.sendDailyStats(selectedBot);
                      alert('✅ Отчёт отправлен!');
                    } catch (e) {
                      alert('❌ Ошибка: ' + e.message);
                    }
                  }}
                >
                  📤 Отправить отчёт сейчас
                </button>
              </>
            )}
          </div>

          {/* Contact Button */}
          <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem' }}>📞 Кнопка «Связаться со мной»</h4>
          <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '2px solid var(--primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <strong>📞 Постоянная кнопка «Связаться»</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={config.contactButton?.enabled === true}
                  onChange={(e) => {
                    const contactButton = { ...(config.contactButton || {}), enabled: e.target.checked };
                    setConfig({ ...config, contactButton });
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
              Постоянная кнопка внизу чата. При нажатии пользователь выбирает способ связи: Telegram, телефон или другой мессенджер.
              Уведомление отправляется в группу из настроек «Уведомление о новом пользователе».
            </p>

            {config.contactButton?.enabled && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>Текст кнопки:</label>
                  <input type="text" className="input" style={{ width: '100%' }}
                    value={config.contactButton?.buttonText ?? '📞 Связаться со мной'}
                    onChange={(e) => {
                      const contactButton = { ...(config.contactButton || {}), buttonText: e.target.value };
                      setConfig({ ...config, contactButton });
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>Вступительный текст (при /start):</label>
                  <textarea className="input" style={{ width: '100%', minHeight: '60px' }}
                    value={config.contactButton?.introText ?? '📞 Вы можете связаться с нами в любое время — кнопка внизу 👇'}
                    onChange={(e) => {
                      const contactButton = { ...(config.contactButton || {}), introText: e.target.value };
                      setConfig({ ...config, contactButton });
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>Текст меню выбора:</label>
                  <input type="text" className="input" style={{ width: '100%' }}
                    value={config.contactButton?.menuText ?? 'Как вам удобнее связаться?'}
                    onChange={(e) => {
                      const contactButton = { ...(config.contactButton || {}), menuText: e.target.value };
                      setConfig({ ...config, contactButton });
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>Текст подтверждения:</label>
                  <textarea className="input" style={{ width: '100%', minHeight: '60px' }}
                    value={config.contactButton?.thankYouText ?? '✅ Спасибо! Ваш персональный консультант свяжется с Вами в течение рабочего дня.'}
                    onChange={(e) => {
                      const contactButton = { ...(config.contactButton || {}), thankYouText: e.target.value };
                      setConfig({ ...config, contactButton });
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>Текст кнопки «Продолжить опрос»:</label>
                  <input type="text" className="input" style={{ width: '100%' }}
                    value={config.contactButton?.continueButtonText ?? '▶️ Продолжить опрос'}
                    onChange={(e) => {
                      const contactButton = { ...(config.contactButton || {}), continueButtonText: e.target.value };
                      setConfig({ ...config, contactButton });
                    }}
                  />
                </div>

                {/* Options */}
                <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Способы связи</h5>

                {/* Telegram option */}
                <div style={{ padding: '0.75rem', marginBottom: '0.5rem', background: '#fff', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>💬 В телеграме</span>
                    <label className="toggle-switch">
                      <input type="checkbox"
                        checked={config.contactButton?.options?.telegram?.enabled !== false}
                        onChange={(e) => {
                          const options = { ...(config.contactButton?.options || {}) };
                          options.telegram = { ...(options.telegram || {}), enabled: e.target.checked };
                          setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <input type="text" className="input" style={{ width: '100%' }}
                    value={config.contactButton?.options?.telegram?.text ?? '💬 В телеграме'}
                    onChange={(e) => {
                      const options = { ...(config.contactButton?.options || {}) };
                      options.telegram = { ...(options.telegram || {}), text: e.target.value };
                      setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                    }}
                  />
                </div>

                {/* Phone option */}
                <div style={{ padding: '0.75rem', marginBottom: '0.5rem', background: '#fff', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>📱 По номеру телефона</span>
                    <label className="toggle-switch">
                      <input type="checkbox"
                        checked={config.contactButton?.options?.phone?.enabled !== false}
                        onChange={(e) => {
                          const options = { ...(config.contactButton?.options || {}) };
                          options.phone = { ...(options.phone || {}), enabled: e.target.checked };
                          setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Текст кнопки</label>
                    <input type="text" className="input" style={{ width: '100%' }}
                      value={config.contactButton?.options?.phone?.text ?? '📱 По номеру телефона'}
                      onChange={(e) => {
                        const options = { ...(config.contactButton?.options || {}) };
                        options.phone = { ...(options.phone || {}), text: e.target.value };
                        setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Запрос номера</label>
                    <textarea className="input" style={{ width: '100%', minHeight: '40px' }}
                      value={config.contactButton?.options?.phone?.promptText ?? 'Нажмите «Поделиться номером» или введите его вручную (+44 999 999 99 99)'}
                      onChange={(e) => {
                        const options = { ...(config.contactButton?.options || {}) };
                        options.phone = { ...(options.phone || {}), promptText: e.target.value };
                        setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Текст кнопки «Поделиться»</label>
                    <input type="text" className="input" style={{ width: '100%' }}
                      value={config.contactButton?.options?.phone?.shareButtonText ?? '📱 Поделиться номером'}
                      onChange={(e) => {
                        const options = { ...(config.contactButton?.options || {}) };
                        options.phone = { ...(options.phone || {}), shareButtonText: e.target.value };
                        setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                      }}
                    />
                  </div>
                </div>

                {/* Messenger option */}
                <div style={{ padding: '0.75rem', marginBottom: '0.5rem', background: '#fff', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>💬 Другой мессенджер</span>
                    <label className="toggle-switch">
                      <input type="checkbox"
                        checked={config.contactButton?.options?.messenger?.enabled !== false}
                        onChange={(e) => {
                          const options = { ...(config.contactButton?.options || {}) };
                          options.messenger = { ...(options.messenger || {}), enabled: e.target.checked };
                          setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Текст кнопки</label>
                    <input type="text" className="input" style={{ width: '100%' }}
                      value={config.contactButton?.options?.messenger?.text ?? '💬 Другой мессенджер'}
                      onChange={(e) => {
                        const options = { ...(config.contactButton?.options || {}) };
                        options.messenger = { ...(options.messenger || {}), text: e.target.value };
                        setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Текст запроса</label>
                    <textarea className="input" style={{ width: '100%', minHeight: '40px' }}
                      value={config.contactButton?.options?.messenger?.promptText ?? 'Напишите как с Вами связаться в свободной форме'}
                      onChange={(e) => {
                        const options = { ...(config.contactButton?.options || {}) };
                        options.messenger = { ...(options.messenger || {}), promptText: e.target.value };
                        setConfig({ ...config, contactButton: { ...(config.contactButton || {}), options } });
                      }}
                    />
                  </div>
                </div>

                {/* Notify Group toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem' }}>
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>🔔 Уведомлять группу</span>
                  <label className="toggle-switch">
                    <input type="checkbox"
                      checked={config.contactButton?.notifyGroup !== false}
                      onChange={(e) => {
                        const contactButton = { ...(config.contactButton || {}), notifyGroup: e.target.checked };
                        setConfig({ ...config, contactButton });
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
                  Использует ID группы из настроек «Уведомление о новом пользователе» выше
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Rules Section */}
      {currentSection === 'rules' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Правила на основе Deeplink параметров</h3>
            <button className="btn btn-primary btn-sm" onClick={addRule}>+ Добавить правило</button>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Изменяйте вопросы на основе параметров в ссылке <code>t.me/bot?start=c-de__s-fb__v-1</code>.
            Правила проверяются по порядку, первое совпадение выполняется.
          </p>

          {(config.rules || []).map((rule, ruleIndex) => (
            <div key={rule.id || ruleIndex} style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  value={rule.name || ''}
                  onChange={(e) => updateRule(ruleIndex, 'name', e.target.value)}
                  style={{ fontWeight: 600, fontSize: '1rem', border: 'none', background: 'transparent', padding: '0' }}
                  placeholder="Название правила"
                />
                <button className="btn btn-danger btn-sm" onClick={() => removeRule(ruleIndex)}>✕</button>
              </div>

              {/* Conditions */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Условия (все должны выполняться):</span>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: '0.75rem' }} onClick={() => addRuleCondition(ruleIndex)}>+ Условие</button>
                </div>
                {rule.conditions.map((cond, condIndex) => (
                  <div key={condIndex} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <select
                      value={cond.param}
                      onChange={(e) => updateRuleCondition(ruleIndex, condIndex, 'param', e.target.value)}
                      style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                    >
                      <option value="country">country (c)</option>
                      <option value="source">source (s)</option>
                      <option value="version">version (v)</option>
                      <option value="company">company (cm)</option>
                      <option value="requestPhoneType">requestPhoneType (rpt)</option>
                      <option value="finalMessage">finalMessage (f)</option>
                    </select>
                    <select
                      value={cond.operator}
                      onChange={(e) => updateRuleCondition(ruleIndex, condIndex, 'operator', e.target.value)}
                      style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                    >
                      <option value="eq">=</option>
                      <option value="neq">≠</option>
                      <option value="in">в списке</option>
                      <option value="notin">не в списке</option>
                      <option value="exists">существует</option>
                      <option value="notexists">не существует</option>
                      <option value="gt">&gt;</option>
                      <option value="gte">≥</option>
                      <option value="lt">&lt;</option>
                      <option value="lte">≤</option>
                    </select>
                    {!['exists', 'notexists'].includes(cond.operator) && (
                      <input
                        type="text"
                        value={cond.value || ''}
                        onChange={(e) => updateRuleCondition(ruleIndex, condIndex, 'value', e.target.value)}
                        style={{ flex: 1, padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                        placeholder={cond.operator === 'in' || cond.operator === 'notin' ? 'de,at,ch (через запятую)' : 'значение'}
                      />
                    )}
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => removeRuleCondition(ruleIndex, condIndex)}
                      style={{ padding: '0.25rem 0.5rem', visibility: rule.conditions.length > 1 ? 'visible' : 'hidden' }}
                    >✕</button>
                  </div>
                ))}
              </div>

              {/* Action */}
              <div style={{ background: 'white', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-200)' }}>
                <span style={{ fontWeight: 500, fontSize: '0.875rem', marginRight: '0.5rem' }}>Действие:</span>
                <select
                  value={rule.action?.type || 'skip'}
                  onChange={(e) => updateRuleAction(ruleIndex, 'type', e.target.value)}
                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem', marginRight: '0.5rem' }}
                >
                  <option value="skip">Пропустить вопросы</option>
                  <option value="replace">Заменить вопрос</option>
                </select>

                {rule.action?.type === 'skip' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Вопросы для пропуска:</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                      {(config.questions || []).map((q, qIdx) => {
                        const isSkipped = (rule.action?.questionIds || []).includes(q.id);
                        return (
                          <button
                            key={q.id}
                            className={`btn btn-sm ${isSkipped ? 'btn-primary' : 'btn-secondary'}`}
                            style={{ fontSize: '0.75rem', padding: '0.125rem 0.375rem' }}
                            onClick={() => {
                              const questionIds = [...(rule.action?.questionIds || [])];
                              if (isSkipped) {
                                const idx = questionIds.indexOf(q.id);
                                if (idx !== -1) questionIds.splice(idx, 1);
                              } else {
                                questionIds.push(q.id);
                              }
                              updateRuleAction(ruleIndex, 'questionIds', questionIds);
                            }}
                          >
                            {qIdx + 1}. {q.id.replace('q', 'Q').replace('_', ': ')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {rule.action?.type === 'replace' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Заменить вопрос:</span>
                      <select
                        value={rule.action?.questionId || ''}
                        onChange={(e) => updateRuleAction(ruleIndex, 'questionId', e.target.value)}
                        style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                      >
                        <option value="">Выберите вопрос</option>
                        {(config.questions || []).map((q, qIdx) => (
                          <option key={q.id} value={q.id}>{qIdx + 1}. {q.id}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Новый текст вопроса:</span>
                      <textarea
                        value={rule.action?.replacement?.text || ''}
                        onChange={(e) => updateRuleAction(ruleIndex, 'replacement', { ...(rule.action?.replacement || {}), text: e.target.value })}
                        rows={2}
                        style={{ width: '100%', marginTop: '0.25rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
                        placeholder="Текст замены (оставьте пустым для использования оригинала)"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {(config.rules || []).length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔀</div>
              <p>Нет правил. Нажмите "Добавить правило" чтобы создать.</p>
              <p style={{ fontSize: '0.875rem' }}>Правила позволяют пропускать или заменять вопросы в зависимости от параметров в deeplink.</p>
            </div>
          )}
        </div>
      )}

      {/* Variants Section */}
      {currentSection === 'variants' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Варианты по источникам трафика</h3>
            <button className="btn btn-primary btn-sm" onClick={addVariant}>+ Добавить вариант</button>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Переопределите контент для разных рекламных кампаний. Ссылка: <code>t.me/bot?start=ПАРАМЕТР</code>. Пустые поля = используется базовое значение.
          </p>

          {Object.entries(config.variants || {}).map(([variantKey, variant]) => (
            <VariantEditor
              key={variantKey}
              variantKey={variantKey}
              variant={variant}
              baseQuestions={config.questions || []}
              onUpdate={(section, field, value) => updateVariant(variantKey, section, field, value)}
              onUpdateQuestions={(questions) => {
                const variants = { ...(config.variants || {}) };
                variants[variantKey] = { ...variants[variantKey], questions };
                setConfig({ ...config, variants });
              }}
              onRemove={() => removeVariant(variantKey)}
            />
          ))}

          {Object.keys(config.variants || {}).length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎯</div>
              <p>Нет вариантов. Нажмите "Добавить вариант" чтобы создать.</p>
              <p style={{ fontSize: '0.875rem' }}>Варианты позволяют показывать разный контент в зависимости от источника трафика.</p>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Variant Editor Component
function VariantEditor({ variantKey, variant, baseQuestions, onUpdate, onUpdateQuestions, onRemove }) {
  const [expandedSection, setExpandedSection] = useState('welcome');

  // Initialize variant questions from base if not set
  const variantQuestions = variant.questions || [];

  const toggleQuestionOverride = (qIndex) => {
    const questions = [...variantQuestions];
    if (questions[qIndex]) {
      // Remove override
      questions[qIndex] = null;
    } else {
      // Add override with copy of base question
      questions[qIndex] = { ...baseQuestions[qIndex] };
    }
    onUpdateQuestions(questions);
  };

  const updateVariantQuestion = (qIndex, field, value) => {
    const questions = [...variantQuestions];
    questions[qIndex] = { ...(questions[qIndex] || baseQuestions[qIndex]), [field]: value };
    onUpdateQuestions(questions);
  };

  return (
    <div style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '2px solid var(--primary)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <strong style={{ fontSize: '1.1rem' }}>🎯 {variantKey}</strong>
          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>t.me/bot?start={variantKey}</div>
        </div>
        <button className="btn btn-danger btn-sm" onClick={onRemove}>✕ Удалить</button>
      </div>

      {/* Section Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        {[
          { id: 'welcome', label: '👋 Приветствие' },
          { id: 'questions', label: '❓ Вопросы' },
          { id: 'notifications', label: '📢 Уведомления' },
        ].map(s => (
          <button
            key={s.id}
            className={`btn btn-sm ${expandedSection === s.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setExpandedSection(s.id)}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Welcome Override */}
      {expandedSection === 'welcome' && (
        <div style={{ background: 'white', padding: '0.75rem', borderRadius: '6px' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginBottom: '0.5rem' }}>
            Оставьте пустым чтобы использовать базовое приветствие
          </p>
          <MediaUploader
            label="Медиа:"
            mediaUrl={variant.welcome?.mediaUrl}
            mediaType={variant.welcome?.mediaType}
            onChangeUrl={(url) => onUpdate('welcome', 'mediaUrl', url)}
            onChangeType={(type) => onUpdate('welcome', 'mediaType', type)}
            compact
          />
          <div style={{ marginTop: '0.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.75rem' }}>
              Текст (caption):
            </label>
            <textarea
              value={variant.welcome?.text || ''}
              onChange={(e) => onUpdate('welcome', 'text', e.target.value)}
              rows={2}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.875rem' }}
              placeholder="Пусто = базовое приветствие"
            />
          </div>
        </div>
      )}

      {/* Questions Override */}
      {expandedSection === 'questions' && (
        <div style={{ background: 'white', padding: '0.75rem', borderRadius: '6px' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginBottom: '0.5rem' }}>
            Включите переключатель чтобы переопределить вопрос для этого варианта
          </p>
          {baseQuestions.map((baseQ, qIndex) => {
            const isOverridden = !!variantQuestions[qIndex];
            const q = variantQuestions[qIndex] || baseQ;
            return (
              <div key={qIndex} style={{
                padding: '0.5rem',
                marginBottom: '0.5rem',
                background: isOverridden ? 'var(--gray-50)' : 'transparent',
                borderRadius: '4px',
                border: isOverridden ? '1px solid var(--primary)' : '1px solid var(--gray-200)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Вопрос {qIndex + 1}
                    {isOverridden && <span style={{ color: 'var(--primary)', marginLeft: '0.5rem' }}>✓ переопределён</span>}
                  </span>
                  <label className="toggle-switch" style={{ transform: 'scale(0.8)' }}>
                    <input
                      type="checkbox"
                      checked={isOverridden}
                      onChange={() => toggleQuestionOverride(qIndex)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {isOverridden ? (
                  <>
                    <MediaUploader
                      label="Медиа:"
                      mediaUrl={q.mediaUrl}
                      mediaType={q.mediaType}
                      onChangeUrl={(url) => updateVariantQuestion(qIndex, 'mediaUrl', url)}
                      onChangeType={(type) => updateVariantQuestion(qIndex, 'mediaType', type)}
                      compact
                    />
                    <textarea
                      value={q.text}
                      onChange={(e) => updateVariantQuestion(qIndex, 'text', e.target.value)}
                      rows={2}
                      style={{ width: '100%', marginTop: '0.25rem', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.75rem' }}
                    />
                  </>
                ) : (
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', whiteSpace: 'pre-wrap' }}>
                    {baseQ.text.substring(0, 100)}{baseQ.text.length > 100 ? '...' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Notifications Override */}
      {expandedSection === 'notifications' && (
        <div style={{ background: 'white', padding: '0.75rem', borderRadius: '6px' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginBottom: '0.5rem' }}>
            Пустое поле = используется базовое уведомление
          </p>
          {['surveyComplete', 'phoneRequest', 'thankYou'].map(notifKey => (
            <div key={notifKey} style={{ marginBottom: '0.75rem', padding: '0.5rem', background: 'var(--gray-50)', borderRadius: '4px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
                {notifKey === 'surveyComplete' ? '🎉 Завершение' : notifKey === 'phoneRequest' ? '📱 Телефон' : '✅ Благодарность'}
              </label>
              <MediaUploader
                mediaUrl={variant.notifications?.[notifKey]?.mediaUrl}
                mediaType={variant.notifications?.[notifKey]?.mediaType}
                onChangeUrl={(url) => {
                  const notifications = { ...(variant.notifications || {}) };
                  notifications[notifKey] = { ...(notifications[notifKey] || {}), mediaUrl: url };
                  onUpdate('notifications', notifKey, notifications[notifKey]);
                }}
                onChangeType={(type) => {
                  const notifications = { ...(variant.notifications || {}) };
                  notifications[notifKey] = { ...(notifications[notifKey] || {}), mediaType: type };
                  onUpdate('notifications', notifKey, notifications[notifKey]);
                }}
                compact
              />
              <input
                type="text"
                value={variant.notifications?.[notifKey]?.text || ''}
                onChange={(e) => {
                  const notifications = { ...(variant.notifications || {}) };
                  notifications[notifKey] = { ...(notifications[notifKey] || {}), text: e.target.value };
                  onUpdate('notifications', notifKey, notifications[notifKey]);
                }}
                style={{ width: '100%', marginTop: '0.25rem', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: '0.75rem' }}
                placeholder="Пусто = базовое"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Media Uploader Component
function MediaUploader({ label, mediaUrl, mediaType, onChangeUrl, onChangeType, compact }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      console.log('Uploading file:', file.name, file.type, file.size);
      const result = await api.uploadMedia(file);
      console.log('Upload result:', result);
      if (result.url) {
        console.log('Setting mediaUrl to:', result.url);
        onChangeUrl(result.url);
      }
      if (result.mediaType) {
        console.log('Setting mediaType to:', result.mediaType);
        onChangeType(result.mediaType);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Ошибка загрузки: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ marginTop: compact ? '0.25rem' : '0.5rem' }}>
      {label && <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: compact ? '0.75rem' : '0.875rem' }}>{label}</label>}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="text"
          value={mediaUrl || ''}
          onChange={(e) => onChangeUrl(e.target.value || null)}
          style={{ flex: 1, padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: compact ? '0.75rem' : '0.875rem' }}
          placeholder="URL медиа или загрузите файл"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{ whiteSpace: 'nowrap' }}
        >
          {uploading ? '⏳' : '📁 Загрузить'}
        </button>
        <select
          value={mediaType || ''}
          onChange={(e) => onChangeType(e.target.value || null)}
          style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)', fontSize: compact ? '0.75rem' : '0.875rem' }}
        >
          <option value="">Без медиа</option>
          <option value="photo">Фото</option>
          <option value="video">Видео</option>
          <option value="animation">GIF</option>
        </select>
      </div>
      {mediaUrl && (
        <div style={{ marginTop: '0.5rem' }}>
          {mediaType === 'photo' || (!mediaType && mediaUrl.match(/\.(jpg|jpeg|png|webp)$/i)) ? (
            <img src={mediaUrl} alt="preview" style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px' }} />
          ) : mediaType === 'video' || (!mediaType && mediaUrl.match(/\.(mp4|webm)$/i)) ? (
            <video src={mediaUrl} style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px' }} controls />
          ) : mediaType === 'animation' || (!mediaType && mediaUrl.match(/\.gif$/i)) ? (
            <img src={mediaUrl} alt="preview" style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px' }} />
          ) : null}
        </div>
      )}
    </div>
  );
}

// Settings Tab
function SettingsTab({ webhookInfo, selectedBot }) {
  const [setting, setSetting] = useState(false);
  const [botConfig, setBotConfig] = useState({ crmEnabled: true }); // CRM enabled by default
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [botToken, setBotToken] = useState('');
  const [botDisplayName, setBotDisplayName] = useState('');
  const [savingToken, setSavingToken] = useState(false);
  const [tokenSaved, setTokenSaved] = useState(false);
  const [deletingBot, setDeletingBot] = useState(false);
  
  // Alert settings state
  const [alertChatId, setAlertChatId] = useState('');
  const [alertLoading, setAlertLoading] = useState(true);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertTesting, setAlertTesting] = useState(false);
  const [alertSaved, setAlertSaved] = useState(false);

  // Load alert settings on mount
  useEffect(() => {
    api.getAlertSettings().then(data => {
      setAlertChatId(data.chatId || '');
      setAlertLoading(false);
    }).catch(() => {
      setAlertLoading(false);
    });
  }, []);

  // Load bot config when selectedBot changes
  useEffect(() => {
    if (selectedBot && selectedBot !== 'all') {
      setLoadingSettings(true);
      api.getBotConfig(selectedBot).then(data => {
        // crmEnabled defaults to true if not set
        setBotConfig({ crmEnabled: true, ...data.config });
        setLoadingSettings(false);
      }).catch(() => {
        setBotConfig({ crmEnabled: true });
        setLoadingSettings(false);
      });
    } else {
      setBotConfig({ crmEnabled: true });
      setLoadingSettings(false);
    }
  }, [selectedBot]);

  // Load bot token when selectedBot changes
  useEffect(() => {
    if (selectedBot && selectedBot !== 'all') {
      api.getBot(selectedBot).then(data => {
        setBotToken(data.token || '');
        setBotDisplayName(data.displayName || '');
        setTokenSaved(false);
      }).catch(() => {
        setBotToken('');
        setBotDisplayName('');
      });
    }
  }, [selectedBot]);

  const handleCrmToggle = async () => {
    if (!selectedBot || selectedBot === 'all') {
      alert('Выберите конкретного бота для изменения настроек CRM');
      return;
    }
    const newValue = botConfig.crmEnabled === false ? true : false; // Toggle: undefined/true -> false, false -> true
    try {
      // Use PATCH for partial update - safer, won't overwrite other config fields
      await api.patchBotConfig(selectedBot, { crmEnabled: newValue });
      setBotConfig(prev => ({ ...prev, crmEnabled: newValue }));
    } catch (e) {
      alert('Ошибка сохранения: ' + e.message);
    }
  };

  const handleSaveToken = async (force = false) => {
    if (!selectedBot || selectedBot === 'all') {
      alert('Выберите конкретного бота');
      return;
    }
    if (!botToken.trim()) {
      alert('Введите токен бота');
      return;
    }
    setSavingToken(true);
    try {
      await api.saveBot(selectedBot, botToken.trim(), botDisplayName.trim() || selectedBot, { force });
      setTokenSaved(true);
      setTimeout(() => setTokenSaved(false), 3000);
    } catch (e) {
      if (!force && e.message.includes('different bot')) {
        const confirmReplace = confirm(
          'Этот токен принадлежит другому боту. Заменить токен для этого алиаса?'
        );
        if (confirmReplace) {
          setSavingToken(false);
          return handleSaveToken(true);
        }
      } else {
        alert('Ошибка сохранения токена: ' + e.message);
      }
    } finally {
      setSavingToken(false);
    }
  };

  const handleSetupBotWebhook = async () => {
    if (!selectedBot || selectedBot === 'all') {
      alert('Выберите конкретного бота');
      return;
    }
    setSetting(true);
    try {
      // Setup webhook using token from KV (single source of truth)
      const result = await api.setupBotWebhook(selectedBot);
      alert(`Webhook настроен успешно для ${selectedBot}!\n\nURL: ${result.webhookUrl}`);
      window.location.reload();
    } catch (e) {
      alert('Ошибка: ' + e.message);
    } finally {
      setSetting(false);
    }
  };

  const handleDeleteBot = async () => {
    if (!selectedBot || selectedBot === 'all') {
      alert('Выберите конкретного бота');
      return;
    }
    if (!confirm(`Удалить бота "${selectedBot}"? Это действие нельзя отменить.`)) {
      return;
    }
    setDeletingBot(true);
    try {
      await api.deleteBot(selectedBot);
      alert('Бот удалён. Страница будет перезагружена.');
      window.location.reload();
    } catch (e) {
      alert('Ошибка удаления: ' + e.message);
    } finally {
      setDeletingBot(false);
    }
  };

  // Alert settings handlers
  const handleSaveAlertSettings = async () => {
    const chatIdNum = alertChatId ? parseInt(alertChatId, 10) : null;
    if (alertChatId && (isNaN(chatIdNum) || chatIdNum <= 0)) {
      alert('Chat ID должен быть положительным целым числом');
      return;
    }
    setAlertSaving(true);
    try {
      await api.saveAlertSettings(chatIdNum);
      setAlertSaved(true);
      setTimeout(() => setAlertSaved(false), 3000);
    } catch (e) {
      alert('Ошибка сохранения: ' + e.message);
    } finally {
      setAlertSaving(false);
    }
  };

  const handleTestAlert = async () => {
    if (!alertChatId) {
      alert('Сначала укажите Chat ID');
      return;
    }
    setAlertTesting(true);
    try {
      const result = await api.testAlert();
      if (result.success) {
        alert('✅ Тестовое уведомление отправлено!');
      } else {
        alert('Ошибка: ' + (result.error || 'Неизвестная ошибка'));
      }
    } catch (e) {
      alert('Ошибка: ' + e.message);
    } finally {
      setAlertTesting(false);
    }
  };

  return (
    <>
      {/* Telegram Bot Token Card */}
      <div className="card">
        <div className="settings-section">
          <h3 className="settings-title">🔐 Telegram Bot</h3>
          {selectedBot && selectedBot !== 'all' ? (
            <>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
                Настройки бота <strong>{selectedBot}</strong>. Токен получите у @BotFather в Telegram.
              </p>
              
              <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem', marginBottom: '1rem' }}>
                <label className="settings-label">Отображаемое имя</label>
                <input
                  type="text"
                  className="input"
                  value={botDisplayName}
                  onChange={(e) => setBotDisplayName(e.target.value)}
                  placeholder="Название для UI"
                  style={{ fontFamily: 'inherit' }}
                />
              </div>

              <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }}>
                <label className="settings-label">Bot Token</label>
                <input
                  type="text"
                  className="input"
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                  style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSaveToken()}
                  disabled={savingToken || !botToken.trim()}
                >
                  {savingToken ? 'Сохранение...' : tokenSaved ? '✅ Сохранено' : '💾 Сохранить'}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSetupBotWebhook}
                  disabled={setting}
                >
                  {setting ? 'Настройка...' : '🔧 Настроить Webhook'}
                </button>
                <button
                  className="btn"
                  style={{ background: 'var(--danger)', color: 'white' }}
                  onClick={handleDeleteBot}
                  disabled={deletingBot}
                >
                  {deletingBot ? 'Удаление...' : '🗑️ Удалить бота'}
                </button>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--gray-500)' }}>
              Выберите конкретного бота в селекторе вверху страницы, чтобы настроить его токен.<br />
              Или нажмите "+" чтобы добавить нового бота.
            </p>
          )}
        </div>
      </div>

      {/* Alert Settings Card */}
      <div className="card">
        <div className="settings-section">
          <h3 className="settings-title">🔔 Уведомления о проблемах</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Chat ID для отправки критических уведомлений (сломанные webhook, боты без токенов).
            Уведомления отправляются через leadbot.
          </p>
          {alertLoading ? (
            <p>Загрузка...</p>
          ) : (
            <>
              <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem', marginBottom: '1rem' }}>
                <label className="settings-label">Telegram Chat ID</label>
                <input
                  type="text"
                  className="input"
                  value={alertChatId}
                  onChange={(e) => setAlertChatId(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="123456789"
                  style={{ fontFamily: 'monospace', maxWidth: '200px' }}
                />
                <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
                  Напишите /start @userinfobot чтобы узнать свой Chat ID
                </small>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveAlertSettings}
                  disabled={alertSaving}
                >
                  {alertSaving ? 'Сохранение...' : alertSaved ? '✅ Сохранено' : '💾 Сохранить'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleTestAlert}
                  disabled={alertTesting || !alertChatId}
                >
                  {alertTesting ? 'Отправка...' : '📤 Тест'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* CRM Settings Card */}
      <div className="card">
        <div className="settings-section">
          <h3 className="settings-title">📤 Интеграция CRM</h3>
          {selectedBot && selectedBot !== 'all' ? (
            <>
              <div className="settings-row">
                <span className="settings-label">Отправка в CRM</span>
                <span className="settings-value">
                  {loadingSettings ? (
                    <span>Загрузка...</span>
                  ) : (
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={botConfig.crmEnabled !== false}
                        onChange={handleCrmToggle}
                        disabled={botConfig.tidyMode === true}
                      />
                      <span className="toggle-slider"></span>
                      <span style={{ marginLeft: '0.5rem' }}>
                        {botConfig.crmEnabled !== false ? '✅ Включено' : '❌ Отключено'}
                      </span>
                    </label>
                  )}
                </span>
              </div>
              {botConfig.tidyMode === true && (
                <p style={{ fontSize: '0.875rem', color: 'var(--warning)', marginTop: '0.5rem' }}>
                  ⚠️ CRM автоматически отключен в Tidy режиме
                </p>
              )}
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginTop: '0.5rem' }}>
                Когда включено, данные пользователей автоматически отправляются в CRM после завершения опроса.
              </p>
            </>
          ) : (
            <p style={{ color: 'var(--gray-500)' }}>
              Выберите конкретного бота для настройки CRM интеграции.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="settings-section">
          <h3 className="settings-title">🤖 Информация о боте</h3>
          {selectedBot && selectedBot !== 'all' && webhookInfo?.bot ? (
            <>
              <div className="settings-row">
                <span className="settings-label">Имя бота</span>
                <span className="settings-value">@{webhookInfo.bot.username}</span>
              </div>
              <div className="settings-row">
                <span className="settings-label">ID</span>
                <span className="settings-value">{webhookInfo.bot.id}</span>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--gray-500)' }}>
              {selectedBot === 'all' ? 'Выберите бота для просмотра информации' : 'Информация загружается...'}
            </p>
          )}
        </div>

        <div className="settings-section">
          <h3 className="settings-title">🔗 Webhook Status</h3>
          {selectedBot && selectedBot !== 'all' && webhookInfo?.webhook ? (
            <>
              <div className="settings-row">
                <span className="settings-label">URL</span>
                <span className="settings-value" style={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                  {webhookInfo.webhook.url || '—'}
                </span>
              </div>
              <div className="settings-row">
                <span className="settings-label">Статус</span>
                <span className="settings-value">
                  {webhookInfo.webhook.url ? (
                    <span className="badge badge-success">Активен</span>
                  ) : (
                    <span className="badge badge-danger">Не настроен</span>
                  )}
                </span>
              </div>
              {webhookInfo.webhook.last_error_message && (
                <div className="settings-row">
                  <span className="settings-label">Последняя ошибка</span>
                  <span className="settings-value" style={{ color: 'var(--danger)' }}>
                    {webhookInfo.webhook.last_error_message}
                  </span>
                </div>
              )}
              <div className="settings-row">
                <span className="settings-label">Pending updates</span>
                <span className="settings-value">{webhookInfo.webhook.pending_update_count || 0}</span>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--gray-500)' }}>
              {selectedBot === 'all' ? 'Выберите бота для просмотра webhook статуса' : 'Статус загружается...'}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="settings-section">
          <h3 className="settings-title">📋 API Endpoints</h3>
          <div className="settings-row">
            <span className="settings-label">Lead-Bot Worker</span>
            <span className="settings-value" style={{ fontSize: '0.75rem' }}>https://lead-bot.urowujqjhhsss.workers.dev</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Admin API</span>
            <span className="settings-value">/admin/*</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Webhook формат</span>
            <span className="settings-value" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>/webhook/{'{alias}'}/{'{token}'}</span>
          </div>
        </div>
      </div>
    </>
  );
}
